import axios, { AxiosInstance } from 'axios';

const BASE = process.env.REACT_APP_API_URL ;

class Api {
  c: AxiosInstance;

  constructor() {
    this.c = axios.create({ baseURL: BASE });
    this.c.interceptors.request.use(cfg => {
      const token = localStorage.getItem('accessToken');
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
    this.c.interceptors.response.use(
      r => r,
      err => {
        // alert(err)
        // Guard against a reload loop: if we're already on a public/auth page (login, reset,
        // or the request was for a background call like localization made before the user is
        // authenticated), a 401 here is expected — don't force another hard reload of a page
        // that's going to immediately fire the same unauthenticated request again on mount.
        // Without this check, App.tsx's boot-time loadLocaleFromServer() call on the Login
        // screen would 401 -> reload -> re-mount -> 401 -> reload forever, also blowing through
        // the API's rate limit (this is the "429 Too Many Requests" + repeated "unauthorized"
        // failure loop reported in production).
        if (err.response?.status === 401) {
          localStorage.removeItem('accessToken');
          const onPublicPage = ['/login', '/reset-password', '/mortuary-portal'].includes(window.location.pathname)
            || window.location.pathname.startsWith('/api/shr/share/');
          if (!onPublicPage) window.location.href = '/login';
        }
        return Promise.reject(err);
      }
    );
  }

  // api.ts
convertDoctorOrderToLabRequest = (doctorOrderId: string) => 
this.c.post(`/laboratory/orders/convert-doctor-order/${doctorOrderId}`);
  // Auth
  login = (d: any) => this.c.post('/auth/login', d);
  verifyLoginMfa = (d: any) => this.c.post('/auth/mfa/verify', d);
  changePassword = (d: any) => this.c.put('/auth/change-password', d);
  forgotPassword = (d: any) => this.c.post('/auth/forgot-password', d);
  resetPassword = (d: any) => this.c.post('/auth/reset-password', d);
  logout = () => this.c.post('/auth/logout', {});
  getNotificationCount = () => this.c.get('/notifications/count');
  register = (d: any) => this.c.post('/auth/register', d);
  me = () => this.c.get('/auth/me');
  getUsers = () => this.c.get('/users');
  createUser = (d: any) => this.c.post('/auth/register', d);

  // Patients
  getPatient = (id: string) => this.c.get(`/patients/${id}`);
  createPatient = (d: any) => this.c.post('/patients', d);
  updatePatient = (id: string, d: any) => this.c.put(`/patients/${id}`, d);

  // Billing — Charge Master
  searchBillingServices = (q: string) => this.c.get('/billing/services/search', { params: { q } });
  shaCpoeCheck = (patientId: string, serviceId: string, extra?: { admissionId?: string; visitId?: string }) =>
    this.c.post('/sha/cpoe-check', { patientId, serviceId, ...(extra || {}) });
  
  getServiceCatalog = (p?: any) => this.c.get('/billing/services', { params: p });
  deleteService = (id: string) => this.c.delete(`/billing/services/${id}`);
  getCorporatePrice = (d: any) => this.c.post('/corporate/price', d);
  getServiceCategories = () => this.c.get('/billing/services/categories');
  syncChargeMasterCatalog = () => this.c.post('/billing/services/sync-catalog');
  createService = (d: any) => this.c.post('/billing/services', d);
  updateService = (id: string, d: any) => this.c.put(`/billing/services/${id}`, d);
  deactivateService = (id: string) => this.c.delete(`/billing/services/${id}`);

  // Billing — Packages
  getBillingPackages = () => this.c.get('/billing/packages');

  // Billing — Bill operations
  getBill = (id: string) => this.c.get(`/billing/${id}`);
  createBill = (d: any) => this.c.post('/billing', d);
  getBillingStats = () => this.c.get('/billing/stats');

  // Billing — Bill operations
  addBillItem = (billId: string, d: any) => this.c.post(`/billing/${billId}/items`, d);
  removeBillItem = (billId: string, itemId: string) => this.c.delete(`/billing/${billId}/items/${itemId}`);
  getPatientBills = (id: string) => this.c.get(`/billing/patient/${id}`);
  processBillPayment = (billId: string, d: any) => this.c.post(`/billing/${billId}/payment`, d);
  applyDiscount = (billId: string, d: any) => this.c.post(`/billing/${billId}/discount`, d);
  processRefund = (billId: string, d: any) => this.c.post(`/billing/${billId}/refund`, d);
  getBillClaims = (billId: string) => this.c.get(`/billing/${billId}/claims`);
  getRefunds = (p?: any) => this.c.get('/billing/refunds', { params: p });

  // Billing — Insurance Claims
  getBillingInsuranceClaims = (p?: any) => this.c.get('/billing/claims', { params: p });
  createBillingInsuranceClaim = (d: any) => this.c.post('/billing/claims', d);
  updateBillingClaimStatus = (id: string, d: any) => this.c.put(`/billing/claims/${id}/status`, d);

  // Billing — Cash Counter
  getCurrentShift = () => this.c.get('/billing/counters/current');
  openCashShift = (d: any) => this.c.post('/billing/counters/open', d);
  closeCashShift = (id: string, d: any) => this.c.put(`/billing/counters/${id}/close`, d);

  // Billing Reports
  getDailyCollection = (p?: any) => this.c.get('/reports/daily-collection', { params: p });
  getOutstandingReport = (p?: any) => this.c.get('/reports/outstanding', { params: p });
  getRefundReport = (p?: any) => this.c.get('/reports/refunds', { params: p });
  getDepartmentRevenue = (p?: any) => this.c.get('/reports/department-revenue', { params: p });
  getCancelledBillsReport = (p?: any) => this.c.get('/reports/cancelled-bills', { params: p });
  getDiscountReport = (p?: any) => this.c.get('/reports/discount-report', { params: p });
  getUserCollectionReport = (p?: any) => this.c.get('/reports/user-collection', { params: p });
  createPatientVisit = (d: any) => this.c.post('/patients/visits', d);
  getPatientStats = () => this.c.get('/patients/stats');
  searchPatients = (q: string) => this.c.get('/patients', { params: { search: q, take: 10 } });
  searchPatientsAdvanced = (params: any) => this.c.get('/patients/search', { params }); 
  // Appointments
  getAppointments = (p?: any) => this.c.get('/appointments', { params: p });
  createAppointment = (d: any) => this.c.post('/appointments', d);
  updateAppointment = (id: string, d: any) => this.c.put(`/appointments/${id}`, d);
  updateAppointmentStatus = (id: string, status: string) => this.c.put(`/appointments/${id}`, { status });
  cancelAppointment = (id: string) => this.c.put(`/appointments/${id}/cancel`);
  getAvailableSlots = (p?: any) => this.c.get('/scheduling/slots', { params: p });

  // Doctor scheduling
  getDoctorSchedules = (doctorId?: string) => this.c.get('/scheduling/schedules', { params: doctorId ? { doctorId } : {} });
  createDoctorSchedule = (d: any) => this.c.post('/scheduling/schedules', d);
  updateDoctorSchedule = (id: string, d: any) => this.c.put(`/scheduling/schedules/${id}`, d);
  deleteDoctorSchedule = (id: string) => this.c.delete(`/scheduling/schedules/${id}`);
  getDoctorLeaves = (p?: any) => this.c.get('/scheduling/leaves', { params: p });
  deleteDoctorLeave = (id: string) => this.c.delete(`/scheduling/leaves/${id}`);
  getTodayQueue = () => this.c.get('/appointments/queue');
  getAppointmentStats = () => this.c.get('/appointments/stats');

  // OPD
  getConsultations = (p?: any) => this.c.get('/opd/consultations', { params: p });
  createConsultation = (d: any) => this.c.post('/opd/consultations', d);
  updateConsultation = (id: string, d: any) => this.c.put(`/opd/consultations/${id}`, d);
  recordVitals = (d: any) => this.c.post('/opd/vitals', d);
  createPrescription = (d: any) => this.c.post('/opd/prescriptions', d);

  // Procedures
  getProcedureCatalog = (params?: any) => this.c.get('/procedures/catalog', { params });
  logProcedure = (d: any) => this.c.post('/procedures', d);
  getPatientProcedures = (patientId: string) => this.c.get(`/procedures/patient/${patientId}`);

  // Follow-ups
  createFollowup = (d: any) => this.c.post('/followups', d);
  getUpcomingFollowups = (params?: any) => this.c.get('/followups/upcoming', { params });
  getOverdueFollowups = (params?: any) => this.c.get('/followups/overdue', { params });
  getPatientFollowups = (patientId: string) => this.c.get(`/followups/patient/${patientId}`);
  updateFollowupStatus = (id: string, status: string) => this.c.put(`/followups/${id}/status`, { status });

  // Token / queue calling status
  updateCallingStatus = (id: string, callingStatus: string) => this.c.put(`/appointments/${id}/calling-status`, { callingStatus });

  // Reports
  getDiagnosisStats = (p?: any) => this.c.get('/reports/diagnosis-stats', { params: p });
  getPrescriptionAnalytics = (p?: any) => this.c.get('/reports/prescription-analytics', { params: p });
  getFollowupReport = (p?: any) => this.c.get('/reports/followup-report', { params: p });
  getPrescriptions = (patientId: string) => this.c.get(`/opd/prescriptions/${patientId}`);
  getOPDStats = () => this.c.get('/opd/stats');
  getOPDQueue = () => this.c.get('/opd/today-queue');

  // IPD
  admitPatient = (d: any) => this.c.post('/ipd/admit', d);
  getAdmission = (id: string) => this.c.get(`/ipd/admissions/${id}`);
  updateAdmission = (id: string, d: any) => this.c.put(`/ipd/admissions/${id}`, d);
  getPatientAdmissions = (patientId: string) => this.c.get(`/ipd/admissions/patient/${patientId}`);
  getCurrentAdmission = (patientId: string) => this.c.get(`/ipd/admissions/patient/${patientId}/current`);
  createWard = (d: any) => this.c.post('/enterprise/wards', d);
  createBed = (d: any) => this.c.post('/ipd/beds', d);
  updateBedStatus = (id: string, d: any) => this.c.put(`/ipd/beds/${id}/status`, d);
  getBedStats = () => this.c.get('/ipd/beds/stats');
  getIPDStats = () => this.c.get('/ipd/stats');
  // IPD — Doctor Orders
  createDoctorOrder = (d: any) => this.c.post('/ipd/orders', d);
  getDoctorOrders = (admissionId: string, p?: any) => this.c.get(`/ipd/orders/${admissionId}`, { params: p });
  updateDoctorOrder = (id: string, d: any) => this.c.put(`/ipd/orders/${id}/status`, d);
  // IPD — Clinical Notes
  addClinicalNote = (d: any) => this.c.post('/ipd/notes', d);
  getClinicalNotes = (admissionId: string, type?: string) => this.c.get(`/ipd/notes/${admissionId}`, { params: type?{type}:{} });
  // IPD — Transfers & Rounds
  getIPDTransfers = (admissionId: string) => this.c.get(`/ipd/transfers/${admissionId}`);
  addDoctorRound = (d: any) => this.c.post('/ipd/rounds', d);
  getDoctorRounds = (admissionId: string) => this.c.get(`/ipd/rounds/${admissionId}`);
  // IPD Reports
  getOccupancyReport = (p?: any) => this.c.get('/ipd/reports/occupancy', { params: p });

  // Billing
  getBillById = (id: string) => this.c.get(`/billing/${id}`);
  processPayment = (id: string, d: any) => this.c.post(`/billing/${id}/payment`, d);
  getRevenueReport = (p?: any) => this.c.get('/reports/revenue', { params: p });

  // IPD Billing — deposits, charges, interim/final bills
  collectIPDDeposit = (d: any) => this.c.post('/billing/ipd/deposits', d);
  getIPDDeposits = (admissionId: string) => this.c.get(`/billing/ipd/${admissionId}/deposits`);
  getIPDCharges = (admissionId: string) => this.c.get(`/billing/ipd/${admissionId}/charges`);
  generateInterimBill = (admissionId: string, d?: any) => this.c.post(`/billing/ipd/${admissionId}/interim`, d || {});
  generateFinalBill = (admissionId: string, d?: any) => this.c.post(`/billing/ipd/${admissionId}/final`, d || {});
  getIPDBillingSummary = (admissionId: string) => this.c.get(`/billing/ipd/${admissionId}/summary`);

  // Pharmacy
  getPharmacyReturns = (p?: any) => this.c.get('/pharmacy/returns/customer', { params: p });
  getDrugs = (p?: any) => this.c.get('/pharmacy/drugs', { params: p });
  createDrug = (d: any) => this.c.post('/pharmacy/drugs', d);
  updateDrug = (id: string, d: any) => this.c.put(`/pharmacy/drugs/${id}`, d);
  getLowStockDrugs = () => this.c.get('/pharmacy/drugs/low-stock');
  addBatch = (d: any) => this.c.post('/pharmacy/batches', d);
  getBatches = (drugId?: string) => this.c.get('/pharmacy/batches', { params: drugId ? { drugId } : {} });
  getExpiringBatches = (days?: number) => this.c.get('/pharmacy/batches/expiring', { params: days ? { days } : {} });
  getSales = (p?: any) => this.c.get('/pharmacy/sales', { params: p });
  createSale = (d: any) => this.c.post('/pharmacy/sales', d);
  getPharmacyStats = () => this.c.get('/pharmacy/stats');
  getPharmacyAnalytics = (p?: any) => this.c.get('/pharmacy/analytics', { params: p });

  // Pharmacy — Vendors
  getVendors = (search?: string) => this.c.get('/pharmacy/vendors', { params: search ? { search } : {} });
  createVendor = (d: any) => this.c.post('/pharmacy/vendors', d);
  updateVendor = (id: string, d: any) => this.c.put(`/pharmacy/vendors/${id}`, d);

  // Pharmacy — Purchase Orders
  getPurchaseOrders = (p?: any) => this.c.get('/pharmacy/purchase-orders', { params: p });
  getPurchaseOrderById = (id: string) => this.c.get(`/pharmacy/purchase-orders/${id}`);
  createPurchaseOrder = (d: any) => this.c.post('/pharmacy/purchase-orders', d);
  updatePurchaseOrder = (id: string, d: any) => this.c.put(`/pharmacy/purchase-orders/${id}`, d);
  receivePurchaseOrder = (id: string, d?: any) => this.c.post(`/pharmacy/purchase-orders/${id}/receive`, d || {});

  // Pharmacy — Dispensing
  dispenseRx = (d: any) => this.c.post('/pharmacy/dispense', d);
  getDispenseHistory = (p?: any) => this.c.get('/pharmacy/dispense', { params: p });

  // Pharmacy — Returns & Adjustments
  customerReturn = (d: any) => this.c.post('/pharmacy/returns/customer', d);
  getCustomerReturns = (patientId?: string) => this.c.get('/pharmacy/returns/customer', { params: patientId ? { patientId } : {} });
  supplierReturn = (d: any) => this.c.post('/pharmacy/returns/supplier', d);
  adjustStock = (d: any) => this.c.post('/pharmacy/stock-adjustments', d);
  getStockAdjustments = (drugId?: string) => this.c.get('/pharmacy/stock-adjustments', { params: drugId ? { drugId } : {} });

  // Pharmacy — Reports
  getPharmacyCurrentStock = () => this.c.get('/pharmacy/reports/current-stock');
  getPharmacyBatchStock = (drugId?: string) => this.c.get('/pharmacy/reports/batch-stock', { params: drugId ? { drugId } : {} });


  // Laboratory — Tests & Panels
  getLabTests = (p?: any) => this.c.get('/laboratory/tests', { params: p });
  createLabTest = (d: any) => this.c.post('/laboratory/tests', d);
  updateLabTest = (id: string, d: any) => this.c.put(`/laboratory/tests/${id}`, d);
  getLabPanels = () => this.c.get('/laboratory/panels');
  createLabPanel = (d: any) => this.c.post('/laboratory/panels', d);

  // Laboratory — Orders
  getLabRequests = (p?: any) => this.c.get('/laboratory/orders', { params: p });
  createLabRequest = (d: any) => this.c.post('/laboratory/orders', d);
  getLabOrder = (id: string) => this.c.get(`/laboratory/orders/${id}`);
  getPatientLabOrders = (patientId: string) => this.c.get(`/laboratory/orders/patient/${patientId}`);
  updateLabStatus = (id: string, d: any) => this.c.put(`/laboratory/orders/${id}/status`, d);

  // Laboratory — Sample Tracking
  getAllLabCultures = (p?: any) => this.c.get('/laboratory/cultures', { params: p });
  collectSample = (d: any) => this.c.post('/laboratory/samples/collect', d);
  updateSampleStatus = (id: string, d: any) => this.c.put(`/laboratory/samples/${id}/status`, d);
  getSampleByBarcode = (barcode: string) => this.c.get(`/laboratory/samples/barcode/${barcode}`);

  // Laboratory — Results
  saveLabResult = (d: any) => this.c.post('/laboratory/results', d);
  getLabResults = (requestId: string) => this.c.get(`/laboratory/results/${requestId}`);
  verifyResult = (id: string, d: any) => this.c.put(`/laboratory/results/${id}/verify`, d);

  // Laboratory — Microbiology & Histo
  getLabCulture = (requestId: string) => this.c.get(`/laboratory/culture/${requestId}`);
  // NOTE: this posts the actual per-patient C&S report (organism/growth/antibiotics) tied to a
  // lab request — do not confuse with saveLabCulture() below, which posts to
  // /laboratory/culture-templates (the organism reference-master editor on
  // LabCultureTemplatePage.tsx). LaboratoryPage.tsx's culture-report form used to call
  // saveLabCulture() by mistake, which silently routed real patient results into the templates
  // table instead of lab_cultures — every "Save Culture Report" appeared to succeed but never
  // actually recorded a patient result.
  saveLabCultureResult = (d: any) => this.c.post('/laboratory/culture', d);
  saveHistopathology = (d: any) => this.c.post('/laboratory/histopathology', d);
  getHistopathology = (requestId: string) => this.c.get(`/laboratory/histopathology/${requestId}`);
  getAllLabHistopathology = (p?: any) => this.c.get('/laboratory/histopathology', { params: p });

  // Laboratory — QC & Reports
  getLabStats = () => this.c.get('/laboratory/stats');
  getLabReports = (p?: any) => this.c.get('/laboratory/reports', { params: p });

  // Radiology
  getRadiologyTests = () => this.c.get('/radiology/services');
  getRadiologyOrders = (p?: any) => this.c.get('/radiology/orders', { params: p });
  getRadiologyOrder = (id: string) => this.c.get(`/radiology/orders/${id}`);
  createRadiologyOrder = (d: any) => this.c.post('/radiology/orders', d);
  updateRadiologyOrder = (id: string, d: any) => this.c.put(`/radiology/orders/${id}`, d);
  cancelRadiologyOrder = (id: string, d: any) => this.c.put(`/radiology/orders/${id}/cancel`, d);
  getRadiologyOrderRecord = (id: string) => this.c.get(`/radiology/orders/${id}/record`);
  getRadiologyServices = (p?: any) => this.c.get('/radiology/services', { params: p });
  createRadiologyService = (d: any) => this.c.post('/radiology/services', d);
  getRadiologyRooms = (modality?: string) => this.c.get('/radiology/rooms', { params: modality?{modality}:{} });
  getRadiologySchedules = (p?: any) => this.c.get('/radiology/schedules', { params: p });
  scheduleRadiologyOrder = (d: any) => this.c.post('/radiology/schedules', d);
  radCheckin = (d: any) => this.c.post('/radiology/checkin', d);
  getRadCheckin = (orderId: string) => this.c.get(`/radiology/checkin/${orderId}`);
  uploadRadStudy = (d: any) => this.c.post('/radiology/studies', d);
  getRadStudy = (orderId: string) => this.c.get(`/radiology/studies/${orderId}`);
  getPatientRadStudies = (patientId: string) => this.c.get(`/radiology/studies/patient/${patientId}`);
  getRadiologyTemplates = (modality?: string) => this.c.get('/radiology/templates', { params: modality?{modality}:{} });
  createRadiologyTemplate = (d: any) => this.c.post('/radiology/templates', d);
  createRadiologyReport = (d: any) => this.c.post('/radiology/reports', d);
  getRadiologyReport = (orderId: string) => this.c.get(`/radiology/reports/order/${orderId}`);
  verifyRadiologyReport = (id: string) => this.c.put(`/radiology/reports/${id}/verify`, {});
  deliverRadiologyReport = (id: string) => this.c.put(`/radiology/reports/${id}/deliver`, {});
  amendRadiologyReport = (id: string, d: any) => this.c.put(`/radiology/reports/${id}/amend`, d);
  getCriticalAlerts = (resolved?: string) => this.c.get('/radiology/alerts', { params: resolved?{resolved}:{} });
  getLabCriticalValues = (p?: any) => this.c.get('/laboratory/critical-values', { params: p });
  acknowledgeCriticalValue = (id: string, d: any) => this.c.put(`/laboratory/critical-values/${id}/acknowledge`, d);
  ackCriticalAlert = (id: string, d: any) => this.c.put(`/radiology/alerts/${id}/acknowledge`, d);
  getTeleradiologyCases = () => this.c.get('/radiology/teleradiology');
  sendTeleradiology = (d: any) => this.c.post('/radiology/teleradiology', d);
  updateTeleradiology = (id: string, d: any) => this.c.put(`/radiology/teleradiology/${id}`, d);
  generateRadiologyBill = (orderId: string) => this.c.post(`/radiology/billing/${orderId}/generate`, {});
  getRadiologyBill = (orderId: string) => this.c.get(`/radiology/billing/${orderId}`);
  getRadiologyStats = () => this.c.get('/radiology/stats');
  getRadiologyReport2 = (p?: any) => this.c.get('/radiology/modality-report', { params: p });
  submitReport = (id: string, d: any) => this.c.put(`/radiology/orders/${id}/report`, d);

  // Blood Bank
  // Blood Bank
  getBloodStock = () => this.c.get('/bloodbank/stock');
  getBloodStockByGroup = () => this.c.get('/bloodbank/stock/by-group');
  updateBloodStock = (d: any) => this.c.post('/bloodbank/stock', d);
  getBloodBankStats = () => this.c.get('/bloodbank/stats');
  // Blood Bank — Donors
  registerBloodDonor = (d: any) => this.c.post('/bloodbank/donors', d);
  getBloodDonor = (id: string) => this.c.get(`/bloodbank/donors/${id}`);
  // Blood Bank — Screening & Collection
  screenDonor = (d: any) => this.c.post('/bloodbank/screening', d);
  collectBlood = (d: any) => this.c.post('/bloodbank/collections', d);
  getBloodDonations = (p?: any) => this.c.get('/bloodbank/donations', { params: p });
  // Blood Bank — Testing
  recordBloodTest = (d: any) => this.c.post('/bloodbank/testing', d);
  // Blood Bank — Components
  getBloodComponents = (p?: any) => this.c.get('/bloodbank/components', { params: p });
  separateComponents = (d: any) => this.c.post('/bloodbank/components/separate', d);
  getExpiringUnits = (days?: number) => this.c.get('/bloodbank/components/expiring', { params: days?{days}:{} });
  // Blood Bank — Requests & Issue
  getBloodRequests = (p?: any) => this.c.get('/bloodbank/requests', { params: p });
  createBloodRequest = (d: any) => this.c.post('/bloodbank/requests', d);
  issueBlood = (id: string) => this.c.put(`/bloodbank/requests/${id}/issue`, {});
  createBloodIssue = (d: any) => this.c.post('/bloodbank/issues', d);
  // Blood Bank — Cross Match
  performCrossMatch = (d: any) => this.c.post('/bloodbank/crossmatch', d);
  // Blood Bank — Transfusion
  recordTransfusion = (d: any) => this.c.post('/bloodbank/transfusions', d);
  getTransfusions = (p?: any) => this.c.get('/bloodbank/transfusions', { params: p });
  // Blood Bank — Camps
  getBloodCamps = () => this.c.get('/bloodbank/camps');
  createBloodCamp = (d: any) => this.c.post('/bloodbank/camps', d);

  // Inventory
  // Inventory
  getInventoryItems = (p?: any) => this.c.get('/inventory/items', { params: p });
  createInventoryItem = (d: any) => this.c.post('/inventory/items', d);
  updateInventoryItem = (id: string, d: any) => this.c.put(`/inventory/items/${id}`, d);
  getLowStockInventory = () => this.c.get('/inventory/items/low-stock');
  getInventoryItemStock = (id: string) => this.c.get(`/inventory/items/${id}/stock`);
  getInventoryStats = () => this.c.get('/inventory/stats');
  getInventoryTransactions = (p?: any) => this.c.get('/inventory/transactions', { params: p });
  addInventoryTransaction = (d: any) => this.c.post('/inventory/transactions', d);
  getWarehouses = () => this.c.get('/inventory/warehouses');
  createWarehouse = (d: any) => this.c.post('/inventory/warehouses', d);
  getStockSummary = (warehouseId?: string) => this.c.get('/inventory/stock', { params: warehouseId?{warehouseId}:{} });
  getExpiringInventory = (days?: number) => this.c.get('/inventory/stock/expiring', { params: days?{days}:{} });
  // Inventory — PR
  getInventoryPRs = (p?: any) => this.c.get('/inventory/pr', { params: p });
  createInventoryPR = (d: any) => this.c.post('/inventory/pr', d);
  updateInventoryPRStatus = (id: string, d: any) => this.c.put(`/inventory/pr/${id}/status`, d);
  // Inventory — PO
  getInventoryPOs = (p?: any) => this.c.get('/inventory/po', { params: p });
  createInventoryPO = (d: any) => this.c.post('/inventory/po', d);
  // Inventory — GRN
  getInventoryGRNs = (p?: any) => this.c.get('/inventory/grn', { params: p });
  createInventoryGRN = (d: any) => this.c.post('/inventory/grn', d);
  postInventoryGRN = (id: string) => this.c.post(`/inventory/grn/${id}/post`, {});
  // Inventory — Issues & Transfers
  getDepartmentIssues = (p?: any) => this.c.get('/inventory/issues', { params: p });
  createDepartmentIssue = (d: any) => this.c.post('/inventory/issues', d);
  getStockTransfers = (p?: any) => this.c.get('/inventory/transfers', { params: p });
  createStockTransfer = (d: any) => this.c.post('/inventory/transfers', d);
  // Inventory — Reports
  getDeptConsumptionReport = (p?: any) => this.c.get('/inventory/reports/department-consumption', { params: p });
  getInventoryValuation = (p?: any) => this.c.get('/inventory/reports/valuation', { params: p });

  // HRMS
  getEmployees = (p?: any) => this.c.get('/hrms/employees', { params: p });
  createEmployee = (d: any) => this.c.post('/hrms/employees', d);
  markAttendance = (d: any) => this.c.post('/hrms/attendance', d);
  getTodayAttendance = () => this.c.get('/hrms/attendance/today');
  getLeaves = (p?: any) => this.c.get('/hrms/leaves', { params: p });
  applyLeave = (d: any) => this.c.post('/hrms/leaves', d);
  approveLeave = (id: string, d: any) => this.c.put(`/hrms/leaves/${id}`, d);
  getHRStats = () => this.c.get('/hr/stats');
  getHRAttendanceSummary = (date?: string) => this.c.get('/hr/attendance-summary', { params: date?{date}:{} });
  getDesignations = (p?: any) => this.c.get('/hr/designations', { params: p });
  createDesignation = (d: any) => this.c.post('/hr/designations', d);
  getShifts = () => this.c.get('/hr/shifts');
  createShift = (d: any) => this.c.post('/hr/shifts', d);
  assignShift = (d: any) => this.c.post('/hr/shifts/assign', d);
  getShiftSchedule = (p?: any) => this.c.get('/hr/shifts/schedule', { params: p });
  getLeaveTypes = () => this.c.get('/hr/leave-types');
  getLeaveBalances = (empId: string) => this.c.get(`/hr/leave-balances/${empId}`);
  initLeaveBalances = (empId: string) => this.c.post(`/hr/leave-balances/${empId}/init`, {});
  getSalaryStructures = () => this.c.get('/hr/salary-structures');
  createSalaryStructure = (d: any) => this.c.post('/hr/salary-structures', d);
  getDoctorCompensation = (empId: string) => this.c.get(`/hr/doctor-compensation/${empId}`);
  setDoctorCompensation = (d: any) => this.c.post('/hr/doctor-compensation', d);
  getPayrollList = (p?: any) => this.c.get('/hrms/payroll', { params: p });
  processPayroll = (d: any) => this.c.post('/hr/payroll/process', d);
  updateEmployee = (id: string, d: any) => this.c.put(`/hrms/employees/${id}`, d);
  getRecruitments = (p?: any) => this.c.get('/hr/recruitment', { params: p });
  createRecruitment = (d: any) => this.c.post('/hr/recruitment', d);
  updateRecruitmentStatus = (id: string, d: any) => this.c.put(`/hr/recruitment/${id}/status`, d);
  getTrainings = (p?: any) => this.c.get('/hr/training', { params: p });
  createTraining = (d: any) => this.c.post('/hr/training', d);
  getPerformanceReviews = (p?: any) => this.c.get('/hr/performance', { params: p });
  createPerformanceReview = (d: any) => this.c.post('/hr/performance', d);
  acknowledgeReview = (id: string) => this.c.put(`/hr/performance/${id}/acknowledge`, {});
  getExits = (p?: any) => this.c.get('/hr/exits', { params: p });
  recordExit = (d: any) => this.c.post('/hr/exits', d);
  updateExitClearance = (id: string, d: any) => this.c.put(`/hr/exits/${id}/clearance`, d);

  // Emergency
  registerEmergency = (d: any) => this.c.post('/emergency', d);
  updateEmergency = (id: string, d: any) => this.c.put(`/emergency/${id}`, d);
  dischargeEmergency = (id: string, d: any) => this.c.put(`/emergency/${id}/discharge`, d);
  getEmergencyReport = (p?: any) => this.c.get('/emergency/v2/report', { params: p });
  registerEmergency2 = (d: any) => this.c.post('/emergency/v2/cases', d);
  getEmergencyCases2 = (p?: any) => this.c.get('/emergency/v2/cases', { params: p });
  getEmergencyCase = (id: string) => this.c.get(`/emergency/v2/cases/${id}`);
  recordTriage = (d: any) => this.c.post('/emergency/v2/triage', d);
  addEmergencyConsultation = (d: any) => this.c.post('/emergency/v2/consultations', d);
  addEmergencyOrder = (d: any) => this.c.post('/emergency/v2/orders', d);
  updateEmergencyOrder = (id: string, d: any) => this.c.put(`/emergency/v2/orders/${id}/status`, d);
  addEmergencyMedication = (d: any) => this.c.post('/emergency/v2/medications', d);
  addEmergencyProcedure = (d: any) => this.c.post('/emergency/v2/procedures', d);
  recordTraumaCase = (d: any) => this.c.post('/emergency/v2/trauma', d);
  createMLC = (d: any) => this.c.post('/emergency/v2/mlc', d);
  notifyPolice = (id: string) => this.c.put(`/emergency/v2/mlc/${id}/notify-police`, {});
  getAmbulances = (status?: string) => this.c.get('/emergency/v2/ambulances', { params: status?{status}:{} });
  updateAmbulanceStatus = (id: string, d: any) => this.c.put(`/emergency/v2/ambulances/${id}/status`, d);
  dispatchAmbulance = (id: string, d: any) => this.c.post(`/emergency/v2/ambulances/${id}/dispatch`, d);
  getObservationBeds = () => this.c.get('/emergency/v2/observation-beds');
  assignObsBed = (id: string, d: any) => this.c.put(`/emergency/v2/observation-beds/${id}/assign`, d);
  releaseObsBed = (id: string) => this.c.put(`/emergency/v2/observation-beds/${id}/release`, {});
  admitFromEmergency = (d: any) => this.c.post('/emergency/v2/admit', d);
  createEmergencyReferral = (d: any) => this.c.post('/emergency/v2/referral', d);
  dischargeFromEmergency = (d: any) => this.c.post('/emergency/v2/discharge', d);
  generateEmergencyBill = (caseId: string) => this.c.post(`/emergency/v2/billing/${caseId}/generate`, {});
  getEmergencyBill = (caseId: string) => this.c.get(`/emergency/v2/billing/${caseId}`);

  // OT Surgery
  getSurgeries = (p?: any) => this.c.get('/ot/surgeries', { params: p });
  scheduleSurgery = (d: any) => this.c.post('/ot/surgeries', d);
  updateSurgery = (id: string, d: any) => this.c.put(`/ot/surgeries/${id}`, d);
  getOTStats = () => this.c.get('/ot/stats');

  // Nursing
  getNursingNotes = (patientId: string) => this.c.get(`/nursing/notes/${patientId}`);
  addNursingNote = (d: any) => this.c.post('/nursing/notes', d);
  getPendingMedications = () => this.c.get('/nursing/medications/pending');
  administerMedication = (id: string, d: any) => this.c.put(`/nursing/medications/${id}/administer`, d);

  // Reports
  getDashboardSummary = () => this.c.get('/reports/dashboard');
  getPatientReport = (p?: any) => this.c.get('/reports/patients', { params: p });
  getOPDReport = (p?: any) => this.c.get('/reports/opd', { params: p });
  getIPDReport = (p?: any) => this.c.get('/reports/ipd', { params: p });
  getPharmacyReport = (p?: any) => this.c.get('/reports/pharmacy', { params: p });
  getLabReport = (p?: any) => this.c.get('/reports/lab-summary', { params: p });

  // Settings
  getSettings = () => this.c.get('/settings');
  getMyBranches = () => this.c.get('/settings/my-branches');
  updateBranch = (id: string, d: any) => this.c.put(`/settings/branches/${id}`, d);
  toggleBranch = (id: string) => this.c.put(`/settings/branches/${id}/toggle`, {});
  createWorkstationPublic = (d: any) => this.c.post('/settings/workstations/public', d);
  updateWorkstation = (id: string, d: any) => this.c.put(`/settings/workstations/${id}`, d);
  removeUserAssignment = (id: string) => this.c.put(`/settings/user-assignments/${id}/remove`, {});
  getStationUsers = (stationId: string) => this.c.get(`/settings/station-users/${stationId}`);
  updateSetting = (key: string, value: string) => this.c.put(`/settings/${key}`, { value });
  bulkUpdateSettings = (d: any) => this.c.post('/settings/bulk', d);
  getDepartments = (p?: any) => this.c.get('/settings/departments', { params: p });
  createDepartment = (d: any) => this.c.post('/settings/departments', d);

  // Telemedicine
  getTelemedicine = (p?: any) => this.c.get('/telemedicine', { params: p });
  scheduleTelemedicine = (d: any) => this.c.post('/telemedicine', d);
  startConsultation = (id: string) => this.c.put(`/telemedicine/${id}/start`);
  endConsultation = (id: string, d: any) => this.c.put(`/telemedicine/${id}/end`, d);
  getTelemedicineStats = () => this.c.get('/telemedicine/stats');

  // NABH
  getNABHSummary = () => this.c.get('/quality/compliance/summary');
  getQualityChecklist = (p?: any) => this.c.get('/quality/compliance/checklist', { params: p });
  getQualityIndicators = () => this.c.get('/quality/indicators/dashboard');
  recordIndicatorData = (d: any) => this.c.post('/quality/kpi', d);
  getAuditStats = () => this.c.get('/quality/audit/stats');

  getSystemAuditLogs = (p?: any) => this.c.get('/audit-logs', { params: p });
  getSystemAuditStats = () => this.c.get('/audit-logs/stats');
  getPatientAuditTrail = (patientId: string) => this.c.get(`/audit-logs/patient/${patientId}`);

  // eMAR
  createMedOrder = (d: any) => this.c.post('/emar/orders', d);
  getMedOrders = (admissionId: string, status?: string) => this.c.get(`/emar/orders/admission/${admissionId}`, { params: status?{status}:{} });
  stopMedOrder = (id: string, d: any) => this.c.put(`/emar/orders/${id}/stop`, d);
  holdMedOrder = (id: string) => this.c.put(`/emar/orders/${id}/hold`, {});
  resumeMedOrder = (id: string) => this.c.put(`/emar/orders/${id}/resume`, {});
  getMARSheet = (admissionId: string, date?: string) => this.c.get(`/emar/mar/${admissionId}`, { params: date?{date}:{} });
  getPendingDoses = (admissionId: string) => this.c.get(`/emar/pending/${admissionId}`);
  administerMed = (d: any) => this.c.post('/emar/administer', d);
  getAdminHistory = (admissionId: string) => this.c.get(`/emar/history/${admissionId}`);
  getEmarStats = (admissionId: string) => this.c.get(`/emar/stats/${admissionId}`);
  getNurseDashboard = (d: any) => this.c.post('/emar/dashboard', d);
  // eMAR — PRN
  createPRN = (d: any) => this.c.post('/emar/prn', d);
  getPRNs = (admissionId: string) => this.c.get(`/emar/prn/${admissionId}`);
  administerPRN = (id: string, d?: any) => this.c.post(`/emar/prn/${id}/administer`, d || {});
  // eMAR — IV
  recordIV = (d: any) => this.c.post('/emar/iv', d);
  updateIV = (id: string, d: any) => this.c.put(`/emar/iv/${id}`, d);
  getIVs = (admissionId: string) => this.c.get(`/emar/iv/${admissionId}`);
  getActiveIVs = (admissionId: string) => this.c.get(`/emar/iv/${admissionId}/active`);
  // eMAR — Alerts
  createMedAlert = (d: any) => this.c.post('/emar/alerts', d);
  getMedAlerts = (admissionId: string) => this.c.get(`/emar/alerts/${admissionId}`);
  ackMedAlert = (id: string, d?: any) => this.c.put(`/emar/alerts/${id}/acknowledge`, d || {});
  // eMAR — Reconciliation
  createReconciliation = (d: any) => this.c.post('/emar/reconciliation', d);
  getReconciliations = (admissionId: string) => this.c.get(`/emar/reconciliation/${admissionId}`);
  completeReconciliation = (id: string) => this.c.put(`/emar/reconciliation/${id}/complete`, {});
  // eMAR — Shift Handover
  createHandover = (d: any) => this.c.post('/emar/handover', d);
  ackHandover = (id: string) => this.c.put(`/emar/handover/${id}/acknowledge`, {});
  getHandovers = (admissionId: string) => admissionId ? this.c.get(`/emar/handover/${admissionId}`) : Promise.resolve({data:[]});
  getPendingHandovers = () => this.c.get('/emar/handover/pending/me');
  saveHandover = (d: any) => this.c.post('/emar/handover', d);
  // eMAR — Audit & Reports
  getMedAuditLog = (admissionId: string) => this.c.get(`/emar/audit/${admissionId}`);
  extendMedSchedules = (admissionId: string) => this.c.post(`/emar/extend/${admissionId}`, {});
  getMissedDoseReport = (admissionId: string, p?: any) => this.c.get(`/emar/reports/missed/${admissionId}`, { params: p });

  // Finance
  getFinanceStats = () => this.c.get('/finance/stats');
  getLedgerAccounts = (type?: string) => this.c.get('/finance/ledger', { params: type?{type}:{} });
  createLedgerAccount = (d: any) => this.c.post('/finance/ledger', d);
  getLedgerBalance = (id: string, p?: any) => this.c.get(`/finance/ledger/${id}/balance`, { params: p });
  createJournalEntry = (d: any) => this.c.post('/finance/journal', d);
  getJournalEntries = (p?: any) => this.c.get('/finance/journal', { params: p });
  reverseJournal = (id: string) => this.c.put(`/finance/journal/${id}/reverse`, {});
  getProfitLoss = (p?: any) => this.c.get('/finance/pnl', { params: p });
  openCashCollection = (d: any) => this.c.post('/finance/cash-collections', d);
  closeCashCollection = (id: string, d: any) => this.c.put(`/finance/cash-collections/${id}/close`, d);
  getCashCollections = (p?: any) => this.c.get('/finance/cash-collections', { params: p });
  recordExpense = (d: any) => this.c.post('/finance/expenses', d);
  getExpenses = (p?: any) => this.c.get('/finance/expenses', { params: p });
  getDailyCollectionSummary = (date?: string) => this.c.get('/finance/daily-collection', { params: date?{date}:{} });

  // Finance Enterprise
  getFinanceEnterpriseStats = () => this.c.get('/finance/enterprise-stats');
  // Bank
  getBankAccounts = () => this.c.get('/finance/bank');
  createBankAccount = (d: any) => this.c.post('/finance/bank', d);
  recordBankTransaction = (d: any) => this.c.post('/finance/bank/transactions', d);
  getBankTransactions = (id: string, p?: any) => this.c.get(`/finance/bank/${id}/transactions`, { params: p });
  reconcileBankTxn = (id: string) => this.c.put(`/finance/bank/transactions/${id}/reconcile`, {});
  // AR
  getAR = (p?: any) => this.c.get('/finance/ar', { params: p });
  createAR = (d: any) => this.c.post('/finance/ar', d);
  recordARPayment = (id: string, d: any) => this.c.put(`/finance/ar/${id}/payment`, d);
  getARAgingSummary = () => this.c.get('/finance/ar/aging');
  // AP
  getAP = (p?: any) => this.c.get('/finance/ap', { params: p });
  createAP = (d: any) => this.c.post('/finance/ap', d);
  payVendor = (id: string, d: any) => this.c.put(`/finance/ap/${id}/pay`, d);
  // Fixed Assets
  getFixedAssets = (p?: any) => this.c.get('/finance/assets', { params: p });
  createFixedAsset = (d: any) => this.c.post('/finance/assets', d);
  runDepreciation = (id: string) => this.c.post(`/finance/assets/${id}/depreciation`, {});
  getDeprSchedule = (id: string) => this.c.get(`/finance/assets/${id}/depreciation`);
  // Budgets
  getBudgets = (p?: any) => this.c.get('/finance/budgets', { params: p });
  createBudget = (d: any) => this.c.post('/finance/budgets', d);
  getBudgetVsActual = (fy: string) => this.c.get('/finance/budgets/vs-actual', { params: {fy} });
  // Payroll
  getPayrollEntries = (month: string) => this.c.get('/finance/payroll', { params: {month} });
  createPayrollEntry = (d: any) => this.c.post('/finance/payroll', d);
  // Expense Categories
  getExpenseCategories = () => this.c.get('/finance/expense-categories');
  // Reports
  getTrialBalance = (asOf?: string) => this.c.get('/finance/trial-balance', { params: asOf?{asOf}:{} });
  getBalanceSheet = (asOf?: string) => this.c.get('/finance/balance-sheet', { params: asOf?{asOf}:{} });
  getGeneralLedger = (accountId: string, p?: any) => this.c.get(`/finance/gl/${accountId}`, { params: p });
  getDayBook = (date?: string) => this.c.get('/finance/day-book', { params: date?{date}:{} });

  // OT Enterprise
  getOTScheduleStats = (date?: string) => this.c.get('/ot/v2/stats', { params: date?{date}:{} });
  getOTRooms = (status?: string) => this.c.get('/ot/v2/rooms', { params: status?{status}:{} });
  updateOTRoomStatus = (id: string, d: any) => this.c.put(`/ot/v2/rooms/${id}/status`, d);
  getOTSchedules = (p?: any) => this.c.get('/ot/v2/schedule', { params: p });
  scheduleOT = (d: any) => this.c.post('/ot/v2/schedule', d);
  getOTScheduleById = (id: string) => this.c.get(`/ot/v2/schedule/${id}`);
  startOTSurgery = (id: string) => this.c.put(`/ot/v2/schedule/${id}/start`, {});
  completeOTSurgery = (id: string) => this.c.put(`/ot/v2/schedule/${id}/complete`, {});
  updateOTScheduleStatus = (id: string, d: any) => this.c.put(`/ot/v2/schedule/${id}/status`, d);
  updateOTChecklist = (id: string, d: any) => this.c.put(`/ot/v2/checklists/${id}`, d);
  saveOTAnaesthesia = (d: any) => this.c.post('/ot/v2/anaesthesia', d);
  addOTNote = (d: any) => this.c.post('/ot/v2/notes', d);
  getOTNotes = (scheduleId: string) => this.c.get(`/ot/v2/notes/${scheduleId}`);

  // OT Spec — Surgery Master
  getSurgeryMaster = (p?: any) => this.c.get('/ot/spec/surgeries', { params: p });
  createSurgeryMaster = (d: any) => this.c.post('/ot/spec/surgeries', d);
  // OT Spec — Surgery Requests
  getSurgeryRequests = (p?: any) => this.c.get('/ot/spec/requests', { params: p });
  createSurgeryRequest = (d: any) => this.c.post('/ot/spec/requests', d);
  approveSurgeryRequest = (id: string) => this.c.put(`/ot/spec/requests/${id}/approve`, {});
  cancelSurgeryRequest = (id: string, d: any) => this.c.put(`/ot/spec/requests/${id}/cancel`, d);
  // OT Spec — Surgical Team
  getOTTeam = (scheduleId: string) => this.c.get(`/ot/spec/team/${scheduleId}`);
  saveOTTeam = (d: any) => this.c.post('/ot/spec/team', d);
  // OT Spec — Pre-Op
  getPreOpAssessment = (scheduleId: string) => this.c.get(`/ot/spec/preop/${scheduleId}`);
  savePreOpAssessment = (d: any) => this.c.post('/ot/spec/preop', d);
  // OT Spec — Implants
  getImplants = (scheduleId: string) => this.c.get(`/ot/spec/implants/${scheduleId}`);
  addImplant = (d: any) => this.c.post('/ot/spec/implants', d);
  removeImplant = (id: string) => this.c.delete(`/ot/spec/implants/${id}`);
  // OT Spec — Consumables
  getConsumables = (scheduleId: string) => this.c.get(`/ot/spec/consumables/${scheduleId}`);
  addConsumable = (d: any) => this.c.post('/ot/spec/consumables', d);
  // OT Spec — Blood Usage
  getOTBloodUsage = (scheduleId: string) => this.c.get(`/ot/spec/blood/${scheduleId}`);
  recordOTBloodUsage = (d: any) => this.c.post('/ot/spec/blood', d);
  // OT Spec — Recovery
  getRecoveryRecord = (scheduleId: string) => this.c.get(`/ot/spec/recovery/${scheduleId}`);
  createRecoveryRecord = (d: any) => this.c.post('/ot/spec/recovery', d);
  updateRecoveryRecord = (id: string, d: any) => this.c.put(`/ot/spec/recovery/${id}`, d);
  addRecoveryVitals = (id: string, d: any) => this.c.post(`/ot/spec/recovery/${id}/vitals`, d);
  // OT Spec — Post-Op
  getPostOpNotes = (scheduleId: string) => this.c.get(`/ot/spec/postop/${scheduleId}`);
  savePostOpNotes = (d: any) => this.c.post('/ot/spec/postop', d);
  // OT Spec — Billing
  getOTBill = (scheduleId: string) => this.c.get(`/ot/spec/billing/${scheduleId}`);
  generateOTBill = (scheduleId: string) => this.c.post(`/ot/spec/billing/${scheduleId}/generate`, {});
  updateOTBill = (id: string, d: any) => this.c.put(`/ot/spec/billing/${id}`, d);
  // OT Spec — Complete Record
  getOTSurgeryRecord = (scheduleId: string) => this.c.get(`/ot/spec/record/${scheduleId}`);
  // OT Spec — Reports
  getOTRevenueReport = (p?: any) => this.c.get('/ot/spec/reports/revenue', { params: p });

  // Consent
  getConsentTemplates = (type?: string) => this.c.get('/consents/templates', { params: type?{type}:{} });
  createConsentTemplate = (d: any) => this.c.post('/consents/templates', d);
  updateConsentTemplate = (id: string, d: any) => this.c.put(`/consents/templates/${id}`, d);
  buildConsentForm = (id: string, p?: any) => this.c.get(`/consents/templates/${id}/build`, { params: p });
  getConsentTypes = () => this.c.get('/consents/types');
  createConsentType = (d: any) => this.c.post('/consents/types', d);
  createConsent = (d: any) => this.c.post('/consents', d);
  createBulkConsents = (d: any) => this.c.post('/consents/bulk', d);
  getConsent = (id: string) => this.c.get(`/consents/${id}`);
  getPatientConsents = (patientId: string, p?: any) => this.c.get(`/consents/patient/${patientId}`, { params: p });
  getPendingConsents = (patientId?: string) => this.c.get('/consents/pending', { params: patientId?{patientId}:{} });
  getExpiringConsents = () => this.c.get('/consents/expiring');
  getConsentStats = (p?: any) => this.c.get('/consents/stats', { params: p });
  getConsentDashboardStats = () => this.c.get('/consents/stats/dashboard');
  signConsent = (id: string, d: any) => this.c.put(`/consents/${id}/sign`, d);
  addConsentSignatory = (d: any) => this.c.post('/consents/signatories', d);
  withdrawConsent = (id: string, d: any) => this.c.put(`/consents/${id}/withdraw`, d);
  getConsentAuditLog = (id: string) => this.c.get(`/consents/${id}/audit`);
  uploadConsentDocument = (id: string, d: any) => this.c.post(`/consents/${id}/documents`, d);
  saveProcedureConsent = (d: any) => this.c.post('/consents/procedure', d);
  saveSurgeryConsent = (d: any) => this.c.post('/consents/surgery', d);
  saveAnaesthesiaConsent = (d: any) => this.c.post('/consents/anaesthesia', d);
  saveBloodConsent = (d: any) => this.c.post('/consents/blood', d);
  recordPatientConsent = (d: any) => this.c.post('/consents/record', d);
  withdrawPatientConsent = (id: string, d: any) => this.c.put(`/consent/${id}/withdraw`, d);

  // Immunizations
  addImmunization = (d: any) => this.c.post('/emr/immunizations', d);
  getImmunizations = (patientId: string) => this.c.get(`/emr/immunizations/${patientId}`);
  updateImmunization = (id: string, d: any) => this.c.put(`/emr/immunizations/${id}`, d);
  deleteImmunization = (id: string) => this.c.put(`/emr/immunizations/${id}/delete`, {});

  // Terminology
  searchICD10 = (q: string) => this.c.get('/terminology/icd10/search', { params: { q } });
  searchICD11 = (q: string) => this.c.get('/terminology/icd11/search', { params: { q } });
  getICD11Code = (code: string) => this.c.get(`/terminology/icd11/${code}`);
  searchSNOMED = (q: string) => this.c.get('/terminology/snomed/search', { params: { q } });
  searchLOINC = (q: string) => this.c.get('/terminology/loinc/search', { params: { q } });
  getVitalLOINC = () => this.c.get('/terminology/loinc/vitals');
  getCommonLabLOINC = () => this.c.get('/terminology/loinc/lab-common');
  getTerminologyStats = () => this.c.get('/terminology/stats');

  // ICU
  getICUAdmissions = (p?: any) => this.c.get('/icu/admissions', { params: p });
  admitToICU = (d: any) => this.c.post('/icu/admissions', d);
  updateICUAdmission = (id: string, d: any) => this.c.put(`/icu/admissions/${id}`, d);
  dischargeFromICU = (id: string) => this.c.put(`/icu/admissions/${id}/discharge`);
  recordICUMonitoring = (d: any) => this.c.post('/icu/monitoring', d);
  getICUMonitoring = (admissionId: string) => this.c.get(`/icu/monitoring/${admissionId}`);
  getICUStats = () => this.c.get('/icu/stats');

  // Diet
  getDietOrders = (p?: any) => this.c.get('/diet/orders', { params: p });
  createDietOrder = (d: any) => this.c.post('/diet/orders', d);
  updateDietOrder = (id: string, d: any) => this.c.put(`/diet/orders/${id}`, d);
  getDietTypes = () => this.c.get('/diet/types');

  // Biomedical
  getEquipment = (p?: any) => this.c.get('/biomedical/equipment', { params: p });
  updateEquipment = (id: string, d: any) => this.c.put(`/biomedical/equipment/${id}`, d);
  getDueForMaintenance = () => this.c.get('/biomedical/equipment/due-maintenance');
  logMaintenance = (d: any) => this.c.post('/biomedical/maintenance', d);
  getMaintenanceHistory = (eqId: string) => this.c.get(`/biomedical/maintenance/${eqId}`);
  getBiomedicalStats = () => this.c.get('/biomedical/stats');

  // Extra appointment methods
  getSlots = (doctorId: string, date: string) => this.c.get('/scheduling/slots', { params: { doctorId, date } });
  getDoctorsList = () => this.c.get('/users?role=DOCTOR');
  getAppointmentById = (id: string) => this.c.get(`/appointments/${id}`);

  // ═══════════════ EMR / EHR (Doctor Module) ═══════════════
  getDoctorDashboard = (doctorId: string) => this.c.get(`/emr/dashboard/${doctorId}`);

  // Clinical Profile
  getClinicalProfile = (patientId: string) => this.c.get(`/emr/profile/${patientId}`);
  updateClinicalProfile = (patientId: string, d: any) => this.c.put(`/emr/profile/${patientId}`, d);

  // Allergies
  getEMRAllergies = (patientId: string) => this.c.get(`/emr/allergies/${patientId}`);
  addEMRAllergy = (d: any) => this.c.post('/emr/allergies', d);
  removeEMRAllergy = (id: string) => this.c.put(`/emr/allergies/${id}/remove`);

  // EMR — Encounters
  createEncounter = (d: any) => this.c.post('/emr/encounters', d);
  getEncounters = (patientId: string, limit?: number) => this.c.get(`/emr/encounters/${patientId}`, { params: limit ? { limit } : {} });

  // EMR — Diagnoses
  saveDiagnosis = (d: any) => this.c.post('/emr/diagnoses', d);
  getDiagnoses = (patientId: string, status?: string) => this.c.get(`/emr/diagnoses/${patientId}`, { params: status ? { status } : {} });

  // EMR — Care Plans
  saveCarePlan = (d: any) => this.c.post('/emr/care-plans', d);
  getCarePlans = (patientId: string) => this.c.get(`/emr/care-plans/${patientId}`);
  updateCarePlan = (id: string, d: any) => this.c.put(`/emr/care-plans/${id}`, d);

  // EMR — Chronic Conditions
  addChronicCondition = (d: any) => this.c.post('/emr/chronic', d);
  getChronicConditions = (patientId: string) => this.c.get(`/emr/chronic/${patientId}`);
  updateChronicCondition = (id: string, d: any) => this.c.put(`/emr/chronic/${id}`, d);

  // EMR — Medical / Surgical / Family History
  addMedicalHistory = (d: any) => this.c.post('/emr/history/medical', d);
  getMedicalHistory = (patientId: string) => this.c.get(`/emr/history/medical/${patientId}`);
  deleteMedicalHistory = (id: string) => this.c.put(`/emr/history/medical/${id}/delete`, {});
  addSurgicalHistory = (d: any) => this.c.post('/emr/history/surgical', d);
  getSurgicalHistory = (patientId: string) => this.c.get(`/emr/history/surgical/${patientId}`);
  deleteSurgicalHistory = (id: string) => this.c.put(`/emr/history/surgical/${id}/delete`, {});
  addFamilyHistory = (d: any) => this.c.post('/emr/history/family', d);
  getFamilyHistory = (patientId: string) => this.c.get(`/emr/history/family/${patientId}`);
  deleteFamilyHistory = (id: string) => this.c.put(`/emr/history/family/${id}/delete`, {});

  // EMR — Clinical Alerts
  createClinicalAlert = (d: any) => this.c.post('/emr/alerts', d);
  getClinicalAlerts = (patientId: string) => this.c.get(`/emr/alerts/${patientId}`);
  acknowledgeClinicalAlert = (id: string) => this.c.put(`/emr/alerts/${id}/acknowledge`, {});

  // EMR — legacy local ICD lookup (its backing table was never seeded, so this always
  // returns empty; diagnosis entry now uses searchICD11 above, which is live). Left here
  // only for back-compat in case anything external still calls it.
  searchIcd = (q: string) => this.c.get('/emr/icd/search', { params: { q } });

  // EMR — Clinical Summary
  getPatientClinicalSummary = (patientId: string) => this.c.get(`/emr/clinical-summary/${patientId}`);

  // CPOE Orders
  getCPOEOrders = (p?: any) => this.c.get('/emr/cpoe', { params: p });
  createCPOEOrder = (d: any) => this.c.post('/emr/cpoe', d);
  updateCPOEStatus = (id: string, d: any) => this.c.put(`/emr/cpoe/${id}/status`, d);
  getCPOEPendingStats = (doctorId?: string) => this.c.get('/emr/cpoe/pending-stats', { params: { doctorId } });

  // Referrals
  getReferrals = (p?: any) => this.c.get('/emr/referrals', { params: p });
  createReferral = (d: any) => this.c.post('/emr/referrals', d);
  updateReferralStatus = (id: string, status: string) => this.c.put(`/emr/referrals/${id}/status`, { status });

  // Documents
  getClinicalDocuments = (patientId: string) => this.c.get(`/emr/documents/${patientId}`);
  addClinicalDocument = (d: any) => this.c.post('/emr/documents', d);

  // Visit History (aggregated)
  getVisitHistory = (patientId: string) => this.c.get(`/emr/visit-history/${patientId}`);

  // ═══════════════ DISCHARGE SUMMARY ═══════════════
  generateDischargeDraft = (admissionId: string) => this.c.get(`/discharge-summary/generate/${admissionId}`);
  getDischargeByAdmission = (admissionId: string) => this.c.get(`/discharge-summary/admission/${admissionId}`);
  // ═══════════════ NURSING INDENT ═══════════════
  getIndents = (p?: any) => this.c.get('/indents', { params: p });
  getIndentStats = () => this.c.get('/indents/stats');
  createIndent = (d: any) => this.c.post('/indents', d);
  approveIndent = (id: string) => this.c.put(`/indents/${id}/approve`);
  rejectIndent = (id: string, reason: string) => this.c.put(`/indents/${id}/reject`, { reason });
  fulfillIndent = (id: string, fulfillments: any[]) => this.c.put(`/indents/${id}/fulfill`, { fulfillments });
  cancelIndent = (id: string) => this.c.put(`/indents/${id}/cancel`);
  postIndentToBill = (id: string) => this.c.put(`/indents/${id}/post-to-bill`);
  // ═══════════════ PAYER / TPA / INSURANCE ═══════════════
  getPayers = (p?: any) => this.c.get('/payers', { params: p });
  getPayerStats = () => this.c.get('/payers/stats');
  getPayerById = (id: string) => this.c.get(`/payers/${id}`);
  createPayer = (d: any) => this.c.post('/payers', d);
  updatePayer = (id: string, d: any) => this.c.put(`/payers/${id}`, d);

  getPayerTariffs = (payerId: string, category?: string) => this.c.get(`/payers/${payerId}/tariffs`, { params: { category } });
  addPayerTariff = (payerId: string, d: any) => this.c.post(`/payers/${payerId}/tariffs`, d);
  updatePayerTariff = (tariffId: string, d: any) => this.c.put(`/payers/tariffs/${tariffId}`, d);
  removePayerTariff = (tariffId: string) => this.c.put(`/payers/tariffs/${tariffId}/remove`);

  getPreAuths = (p?: any) => this.c.get('/payers/preauth', { params: p });
  createPreAuth = (d: any) => this.c.post('/payers/preauth', d);
  updatePreAuth = (id: string, d: any) => this.c.put(`/payers/preauth/${id}`, d);

  calculatePayerSplit = (payerId: string, amount: number, sanctioned?: number) =>
    this.c.get(`/payers/split/${payerId}`, { params: { amount, sanctioned } });

  // Insurance — Packages
  getInsurancePackages = (p?: any) => this.c.get('/insurance/packages', { params: p });
  createInsurancePackage = (d: any) => this.c.post('/insurance/packages', d);

  // Insurance — Policies
  getInsurancePolicies = (p?: any) => this.c.get('/insurance/policies', { params: p });
  createInsurancePolicy = (d: any) => this.c.post('/insurance/policies', d);
  updateInsurancePolicy = (id: string, d: any) => this.c.put(`/insurance/policies/${id}`, d);
  checkPolicyValidity = (id: string) => this.c.get(`/insurance/policies/${id}/validity`);

  // Insurance — Beneficiary
  verifyBeneficiary = (d: any) => this.c.post('/insurance/verify', d);
  getBeneficiaryVerifications = (patientId: string) => this.c.get(`/insurance/verify/${patientId}`);

  // Insurance — Pre-Auth
  getInsurancePreAuths = (p?: any) => this.c.get('/insurance/preauth', { params: p });
  respondToPreAuth = (id: string, d: any) => this.c.post(`/insurance/preauth/${id}/respond`, d);

  // Insurance — Claims
  getInsuranceClaims = (p?: any) => this.c.get('/insurance/claims', { params: p });
  createInsuranceClaim = (d: any) => this.c.post('/insurance/claims', d);
  updateInsuranceClaimStatus = (id: string, d: any) => this.c.put(`/insurance/claims/${id}/status`, d);
  addClaimDocument = (id: string, d: any) => this.c.post(`/insurance/claims/${id}/documents`, d);
  getClaimDocuments = (id: string) => this.c.get(`/insurance/claims/${id}/documents`);

  // Insurance — Queries
  getInsuranceQueries = (p?: any) => this.c.get('/insurance/queries', { params: p });
  addClaimQuery = (id: string, d: any) => this.c.post(`/insurance/claims/${id}/queries`, d);
  respondToClaimQuery = (id: string, d: any) => this.c.put(`/insurance/queries/${id}/respond`, d);

  // Insurance — Settlements
  getInsuranceSettlements = (p?: any) => this.c.get('/insurance/settlements', { params: p });
  createInsuranceSettlement = (d: any) => this.c.post('/insurance/settlements', d);
  markSettlementPaid = (id: string, d: any) => this.c.put(`/insurance/settlements/${id}/paid`, d);

  // Insurance — Corporate
  getCorporateAccounts = (search?: string) => this.c.get('/insurance/corporate', { params: search?{search}:{} });
  createCorporateAccount = (d: any) => this.c.post('/insurance/corporate', d);

  // Insurance — Stats & Reports
  getInsuranceStats = () => this.c.get('/insurance/stats');
  getInsuranceReport = (p?: any) => this.c.get('/insurance/reports', { params: p });


  getDischargeByPatient = (patientId: string) => this.c.get(`/discharge-summary/patient/${patientId}`);
  createDischargeSummary = (d: any) => this.c.post('/discharge-summary', d);
  updateDischargeSummary = (id: string, d: any) => this.c.put(`/discharge-summary/${id}`, d);
  finalizeDischargeSummary = (id: string) => this.c.put(`/discharge-summary/${id}/finalize`);


  // Notifications
  sendSMS = (d: any) => this.c.post('/notifications/sms', d);
  sendWhatsApp = (d: any) => this.c.post('/notifications/whatsapp', d);
  sendEmail = (d: any) => this.c.post('/notifications/email', d);
  sendBulkSMS = (d: any) => this.c.post('/notifications/bulk-sms', d);
  getNotifications = (p?: any) => this.c.get('/notifications', { params: p });
  markNotificationRead = (id: string) => this.c.put(`/notifications/${id}/read`);
  getUnreadCount = () => this.c.get('/notifications/count');

  // Analytics
  getAnalyticsDashboard = () => this.c.get('/analytics/dashboard');
  getAnalyticsKpi = () => this.c.get('/analytics/kpi');
  getAnalyticsRevenue = (p?: any) => this.c.get('/analytics/financial', { params: p });
  getAnalyticsOpd = (p?: any) => this.c.get('/analytics/opd', { params: p });
  getAnalyticsIpd = (p?: any) => this.c.get('/analytics/ipd', { params: p });
  getAnalyticsEmergency = (p?: any) => this.c.get('/analytics/emergency', { params: p });
  getAnalyticsLab = (p?: any) => this.c.get('/analytics/lab', { params: p });
  getAnalyticsRadiology = (p?: any) => this.c.get('/analytics/radiology', { params: p });
  getAnalyticsOt = (p?: any) => this.c.get('/analytics/ot', { params: p });
  getAnalyticsPharmacy = (p?: any) => this.c.get('/analytics/pharmacy', { params: p });
  getAnalyticsFinancial = (p?: any) => this.c.get('/analytics/financial', { params: p });
  getAnalyticsPatient = (p?: any) => this.c.get('/analytics/patient', { params: p });
  getAnalyticsDoctor = (p?: any) => this.c.get('/analytics/doctor', { params: p });
  getAnalyticsClinical = (p?: any) => this.c.get('/analytics/clinical', { params: p });
  getAnalyticsInsurance = (p?: any) => this.c.get('/analytics/insurance', { params: p });
  getAnalyticsBloodBank = (p?: any) => this.c.get('/analytics/blood-bank', { params: p });
  getAnalyticsInventory = (p?: any) => this.c.get('/analytics/inventory', { params: p });
  // Nursing Enterprise
  getNursingDashboard = (p?: any) => this.c.get('/nursing/dashboard', { params: p });
  getNursingStations = () => this.c.get('/nursing/stations');
  assignNurse = (d: any) => this.c.post('/nursing/assignments', d);
  getNursingVitals = (patientId: string, p?: any) => this.c.get(`/nursing/vitals/${patientId}`, { params: p });
  addIOEntry = (d: any) => this.c.post('/nursing/io', d);
  getNursingIO = (patientId: string, p?: any) => this.c.get(`/nursing/io/${patientId}`, { params: p });
  createShiftHandover = (d: any) => this.c.post('/nursing/handover', d);
  getNursingHandovers = (ward?: string) => this.c.get('/nursing/handover', { params: ward?{ward}:{} });
  acknowledgeHandover = (id: string, d: any) => this.c.put(`/nursing/handover/${id}/acknowledge`, d);
  addWardRoundNote = (d: any) => this.c.post('/nursing/round-notes', d);
  getWardRoundNotes = (patientId: string) => this.c.get(`/nursing/round-notes/${patientId}`);
  recordNursingProcedure = (d: any) => this.c.post('/nursing/procedures', d);
  getNursingProcedures = (patientId?: string) => this.c.get(patientId ? `/nursing/procedures/${patientId}` : '/nursing/procedures/pending/all');
  saveFallRisk = (d: any) => this.c.post('/nursing/fall-risk', d);
  getFallRisk = (patientId: string) => this.c.get(`/nursing/fall-risk/${patientId}`);
  savePressureUlcer = (d: any) => this.c.post('/nursing/pressure-ulcer', d);
  getPressureUlcerHistory = (patientId: string) => this.c.get(`/nursing/pressure-ulcer/${patientId}`);
  saveDischargeChecklist = (d: any) => this.c.post('/nursing/discharge-checklist', d);
  getDischargeChecklist = (patientId: string, admissionId?: string) => this.c.get(`/nursing/discharge-checklist/${patientId}`, { params: admissionId?{admissionId}:{} });
  getPatientNursingRecord = (patientId: string) => this.c.get(`/nursing/patient/${patientId}/record`);
  createCarePlan = (d: any) => this.c.post('/nursing/care-plans', d);
  recordIVFluid = (d: any) => this.c.post('/nursing/iv-fluids', d);
  recordFluidBalance = (d: any) => this.c.post('/nursing/fluid-balance', d);
  recordWoundCare = (d: any) => this.c.post('/nursing/wound', d);
  recordHandover = (d: any) => this.c.post('/nursing/handover', d);
  createNursingAssessment = (d: any) => this.c.post('/nursing/assessment', d);
  // Diet
  orderDiet = (d: any) => this.c.post('/diet/orders', d);
  discontinueDiet = (id: string) => this.c.put(`/diet/orders/${id}/discontinue`, {});
  nutritionalAssessment = (d: any) => this.c.post('/diet/assessments', d);
  createDietMaster = (d: any) => this.c.post('/diet/master', d);
  // Biomedical
  getMaintenanceSchedule = (p?: any) => this.c.get('/biomedical/maintenance', { params: p });
  schedulePM = (d: any) => this.c.post('/biomedical/maintenance', d);
  reportBreakdown = (d: any) => this.c.post('/biomedical/breakdowns', d);
  updateBreakdownStatus = (id: string, d: any) => this.c.put(`/biomedical/breakdowns/${id}/status`, d);
  recordCalibration = (d: any) => this.c.post('/biomedical/calibrations', d);
  // RBAC
  getRbacDashboard = () => this.c.get('/rbac/dashboard');
  getPermissionMatrix = () => this.c.get('/rbac/permission-matrix');
  getOrgUnits = () => this.c.get('/rbac/org-units');
  createRbacUser = (d: any) => this.c.post('/rbac/users', d);
  updateRbacUser = (id: string, d: any) => this.c.put(`/rbac/users/${id}`, d);
  lockUser = (id: string, d: any) => this.c.put(`/rbac/users/${id}/lock`, d);
  unlockUser = (id: string) => this.c.put(`/rbac/users/${id}/unlock`, {});
  resetUserPassword = (id: string, d: any) => this.c.put(`/rbac/users/${id}/reset-password`, d);
  getUserRoles = (id: string) => this.c.get(`/rbac/users/${id}/roles`);
  getUserPermissions = (id: string) => this.c.get(`/rbac/users/${id}/permissions`);
  getUserLoginHistory = (id: string) => this.c.get(`/rbac/users/${id}/login-history`);
  assignUserRole = (id: string, d: any) => this.c.post(`/rbac/users/${id}/assign-role`, d);
  grantUserPermission = (id: string, d: any) => this.c.post(`/rbac/users/${id}/permissions`, d);
  logoutAllSessions = (id: string) => this.c.put(`/rbac/users/${id}/logout-all`, {});
  invalidateSession = (id: string) => this.c.put(`/rbac/sessions/${id}/invalidate`, {});
  getMySessions = () => this.c.get('/rbac/my-sessions');
  invalidateMySession = (id: string) => this.c.put(`/rbac/my-sessions/${id}/invalidate`, {});
  logoutOtherSessions = () => this.c.post('/rbac/my-sessions/logout-others', {});
  getRoles = () => this.c.get('/rbac/roles');
  createRole = (d: any) => this.c.post('/rbac/roles', d);
  updateRole = (id: string, d: any) => this.c.put(`/rbac/roles/${id}`, d);
  getRolePermissions = (id: string) => this.c.get(`/rbac/roles/${id}/permissions`);
  setRolePermissions = (id: string, d: any) => this.c.put(`/rbac/roles/${id}/permissions`, d);
  getAllPermissions = (module?: string) => this.c.get('/rbac/permissions', { params: module?{module}:{} });
  getAllLoginHistory = (p?: any) => this.c.get('/rbac/login-history', { params: p });
  // DMS
  getDmsStats = () => this.c.get('/dms/stats');
  getDmsCompliance = () => this.c.get('/dms/compliance');
  getDmsExpiring = (days?: number) => this.c.get('/dms/expiring', { params: days?{days}:{} });
  getDmsCategories = (entityType?: string) => this.c.get('/dms/categories', { params: entityType?{entityType}:{} });
  searchDocuments = (p?: any) => this.c.get('/dms/search', { params: p });
  getDmsPendingApprovals = () => this.c.get('/dms/approvals/pending');
  uploadDocument = (d: any) => this.c.post('/dms/upload', d);
  autoRegisterDocument = (d: any) => this.c.post('/dms/auto-register', d);
  getDocument = (id: string) => this.c.get(`/dms/${id}`);
  uploadNewVersion = (id: string, d: any) => this.c.put(`/dms/${id}/version`, d);
  archiveDocument = (id: string, d: any) => this.c.put(`/dms/${id}/archive`, d);
  restoreDocument = (id: string) => this.c.put(`/dms/${id}/restore`, {});
  submitForApproval = (id: string, d: any) => this.c.put(`/dms/${id}/approve`, d);
  signDocument = (id: string, d: any) => this.c.put(`/dms/${id}/sign`, d);
  shareDocument = (id: string, d: any) => this.c.post(`/dms/${id}/share`, d);
  addDocumentTags = (id: string, d: any) => this.c.post(`/dms/${id}/tags`, d);
  removeDocumentTag = (id: string, tag: string) => this.c.delete(`/dms/${id}/tags/${tag}`);
  getDocumentAudit = (id: string) => this.c.get(`/dms/${id}/audit`);
  processApproval = (id: string, d: any) => this.c.put(`/dms/approvals/${id}/process`, d);
  // CPOE
  getCpoeStats = () => this.c.get('/cpoe/stats');
  getCpoeOrderSets = () => this.c.get('/cpoe/order-sets');
  getCpeQueue = (dept: string) => this.c.get(`/cpoe/queue/${dept}`);
  getCpoeDashboard = (doctorId: string) => this.c.get(`/cpoe/dashboard/${doctorId}`);
  validateOrder = (p: any) => this.c.get('/cpoe/validate', { params: p });
  getPatientCpoeOrders = (pid: string, p?: any) => this.c.get(`/cpoe/patient/${pid}`, { params: p });
  getOrderTimeline = (orderId: string) => this.c.get(`/cpoe/timeline/${orderId}`);
  applyCpoeOrderSet = (setId: string, d: any) => this.c.post(`/cpoe/order-set/${setId}`, d);
  updateCpoeStatus = (id: string, d: any) => this.c.put(`/cpoe/orders/${id}/status`, d);
  // Procurement
  getProcurementDashboard = () => this.c.get('/procurement/dashboard');
  getProcurementAnalytics = () => this.c.get('/procurement/analytics');
  getProcurementVendors = (p?: any) => this.c.get('/procurement/vendors', { params: p });
  createProcurementVendor = (d: any) => this.c.post('/procurement/vendors', d);
  updateProcurementVendor = (id: string, d: any) => this.c.put(`/procurement/vendors/${id}`, d);
  blacklistVendor = (id: string, d: any) => this.c.put(`/procurement/vendors/${id}/blacklist`, d);
  getProcurementVendor = (id: string) => this.c.get(`/procurement/vendors/${id}`);
  addVendorDocument = (id: string, d: any) => this.c.post(`/procurement/vendors/${id}/documents`, d);
  getVendorPerformanceHistory = (id: string) => this.c.get(`/procurement/vendors/${id}/performance`);
  getProcurementInvoices = (p?: any) => this.c.get('/procurement/invoices', { params: p });
  createProcurementInvoice = (d: any) => this.c.post('/procurement/invoices', d);
  approveProcurementInvoice = (id: string) => this.c.put(`/procurement/invoices/${id}/approve`, {});
  getOverdueInvoices = () => this.c.get('/procurement/invoices/overdue');
  getProcurementPayments = (p?: any) => this.c.get('/procurement/payments', { params: p });
  createProcurementPayment = (d: any) => this.c.post('/procurement/payments', d);
  getVendorContracts = (p?: any) => this.c.get('/procurement/contracts', { params: p });
  createVendorContract = (d: any) => this.c.post('/procurement/contracts', d);
  getExpiringContracts = (days?: number) => this.c.get('/procurement/contracts/expiring', { params: days?{days}:{} });
  saveVendorPerformance = (d: any) => this.c.post('/procurement/performance', d);
  getProcurementReturns = (p?: any) => this.c.get('/procurement/returns', { params: p });
  createProcurementReturn = (d: any) => this.c.post('/procurement/returns', d);
  getProcurementApprovals = () => this.c.get('/procurement/approvals/pending');
  processApprovalDecision = (id: string, d: any) => this.c.put(`/procurement/approvals/${id}/process`, d);
  // Biomedical Enterprise
  getBiomedicalDashboard = () => this.c.get('/biomedical/enterprise/dashboard');
  getNabhCompliance = () => this.c.get('/biomedical/enterprise/nabh-compliance');
  getBiomedicalMTTR = (equipmentId?: string) => this.c.get('/biomedical/enterprise/mttr', { params: equipmentId?{equipmentId}:{} });
  getDowntimeSummary = (equipmentId?: string) => this.c.get('/biomedical/enterprise/downtime-summary', { params: equipmentId?{equipmentId}:{} });
  getHighUtilization = () => this.c.get('/biomedical/enterprise/high-utilization');
  getOverdueSafetyChecks = () => this.c.get('/biomedical/enterprise/safety-overdue');
  getBiomedicalServiceRequests = (p?: any) => this.c.get('/biomedical/service-requests', { params: p });
  createServiceRequest = (d: any) => this.c.post('/biomedical/service-requests', d);
  updateServiceRequest = (id: string, d: any) => this.c.put(`/biomedical/service-requests/${id}`, d);
  getEquipmentLocationHistory = (id: string) => this.c.get(`/biomedical/equipment/${id}/location-history`);
  updateEquipmentLocation = (id: string, d: any) => this.c.post(`/biomedical/equipment/${id}/location`, d);
  getEquipmentInstallation = (id: string) => this.c.get(`/biomedical/equipment/${id}/installation`);
  registerEquipmentInstallation = (id: string, d: any) => this.c.post(`/biomedical/equipment/${id}/installation`, d);
  getEquipmentUtilization = (id: string, months?: number) => this.c.get(`/biomedical/equipment/${id}/utilization`, { params: months?{months}:{} });
  recordEquipmentUtilization = (id: string, d: any) => this.c.post(`/biomedical/equipment/${id}/utilization`, d);
  getEquipmentSafetyChecks = (id: string) => this.c.get(`/biomedical/equipment/${id}/safety-checks`);
  recordSafetyCheck = (id: string, d: any) => this.c.post(`/biomedical/equipment/${id}/safety-checks`, d);
  decommissionEquipment = (id: string, d: any) => this.c.post(`/biomedical/equipment/${id}/decommission`, d);
  // Audit Enterprise
  getAuditDashboard = () => this.c.get('/audit/dashboard');
  getAuditSecurityEvents = () => this.c.get('/audit/security-events');
  getAuditHeatmap = (days?: number) => this.c.get('/audit/heatmap', { params: days?{days}:{} });
  getAuditCompliance = () => this.c.get('/audit/compliance');
  getAuditIntegrity = () => this.c.get('/audit/integrity');
  getAuditAccessLogs = (patientId?: string) => this.c.get('/audit/access-logs', { params: patientId?{patientId}:{} });
  getMostAccessedPatients = (days?: number) => this.c.get('/audit/most-accessed', { params: days?{days}:{} });
  getAuditExportLogs = (userId?: string) => this.c.get('/audit/export-logs', { params: userId?{userId}:{} });
  getAuditLoginHistory = (p?: any) => this.c.get('/audit/login-history', { params: p });
  searchAuditLogs = (p?: any) => this.c.get('/audit/logs', { params: p });
  getUserAuditActivity = (userId: string) => this.c.get(`/audit/logs/user/${userId}`);
  resolveAuditAlert = (id: string, d: any) => this.c.put(`/audit/alerts/${id}/resolve`, d);
  // Audit
  verifyLogIntegrity = () => this.c.get('/audit/integrity');
  logAuditEvent = (d: any) => this.c.post('/audit/log', d);
  logAuditExport = (d: any) => this.c.post('/audit/export', d);
  // Notifications Enterprise
  getNotificationDashboard = () => this.c.get('/notifications/enterprise/dashboard');
  getDeliveryStats = (days?: number) => this.c.get('/notifications/enterprise/delivery-stats', { params: days?{days}:{} });
  getQueueStatus = () => this.c.get('/notifications/enterprise/queue-status');
  processQueue = () => this.c.post('/notifications/enterprise/queue/process', {});
  getNotificationTemplates = (p?: any) => this.c.get('/notifications/templates', { params: p });
  getNotificationTemplate = (code: string) => this.c.get(`/notifications/templates/${code}`);
  createNotificationTemplate = (d: any) => this.c.post('/notifications/templates', d);
  updateNotificationTemplate = (id: string, d: any) => this.c.put(`/notifications/templates/${id}`, d);
  approveNotificationTemplate = (id: string) => this.c.put(`/notifications/templates/${id}/approve`, {});
  getNotificationLogs = (p?: any) => this.c.get('/notifications/logs', { params: p });
  sendFromTemplate = (d: any) => this.c.post('/notifications/send-template', d);
  enqueueNotification = (d: any) => this.c.post('/notifications/enqueue', d);
  bulkSendNotifications = (d: any) => this.c.post('/notifications/bulk-send', d);
  getNotificationPreferences = () => this.c.get('/notifications/preferences');
  saveNotificationPreferences = (d: any) => this.c.put('/notifications/preferences', d);
  getNotificationSchedules = () => this.c.get('/notifications/schedules');
  createNotificationSchedule = (d: any) => this.c.post('/notifications/schedules', d);
  // Patient Portal
  portalRegister = (d: any) => this.c.post('/patient-portal/register', d);
  portalVerifyOtp = (d: any) => this.c.post('/patient-portal/verify-otp', d);
  portalStats = () => this.c.get('/patient-portal/stats');
  portalDashboard = () => this.c.get('/patient-portal/dashboard');
  portalGetAppointments = (status?: string) => this.c.get('/patient-portal/appointments', { params: status?{status}:{} });
  portalBookAppointment = (d: any) => this.c.post('/patient-portal/appointments', d);
  portalCancelAppointment = (id: string, d: any) => this.c.put(`/patient-portal/appointments/${id}/cancel`, d);
  portalGetLabReports = () => this.c.get('/patient-portal/lab-reports');
  portalGetRadiologyReports = () => this.c.get('/patient-portal/radiology-reports');
  portalGetVitals = (limit?: number) => this.c.get('/patient-portal/vitals', { params: limit?{limit}:{} });
  portalGetPrescriptions = () => this.c.get('/patient-portal/prescriptions');
  portalGetDiagnoses = () => this.c.get('/patient-portal/diagnoses');
  portalGetBills = () => this.c.get('/patient-portal/bills');
  portalGetPayments = () => this.c.get('/patient-portal/payments');
  portalGetDocuments = () => this.c.get('/patient-portal/documents');
  portalGetAdmission = () => this.c.get('/patient-portal/admission');
  portalGetFamily = () => this.c.get('/patient-portal/family');
  portalAddFamilyMember = (d: any) => this.c.post('/patient-portal/family', d);
  portalSubmitFeedback = (d: any) => this.c.post('/patient-portal/feedback', d);
  portalGetFeedback = () => this.c.get('/patient-portal/feedback');
  portalGetTelemedicine = () => this.c.get('/patient-portal/telemedicine');
  portalScheduleTelemedicine = (d: any) => this.c.post('/patient-portal/telemedicine', d);
  portalGetNotifications = () => this.c.get('/patient-portal/notifications');
  portalMarkNotifRead = (id: string) => this.c.put(`/patient-portal/notifications/${id}/read`, {});
  portalMarkAllRead = () => this.c.put('/patient-portal/notifications/read-all', {});
  portalGetTimeline = () => this.c.get('/patient-portal/timeline');
  // Workflow Engine
  getWorkflowDashboard = () => this.c.get('/workflow/dashboard');
  getWorkflowAnalytics = (days?: number) => this.c.get('/workflow/analytics', { params: days?{days}:{} });
  getWorkflows = (p?: any) => this.c.get('/workflow', { params: p });
  getWorkflow = (id: string) => this.c.get(`/workflow/${id}`);
  createWorkflow = (d: any) => this.c.post('/workflow', d);
  updateWorkflow = (id: string, d: any) => this.c.put(`/workflow/${id}`, d);
  publishWorkflow = (id: string) => this.c.post(`/workflow/${id}/publish`, {});
  unpublishWorkflow = (id: string) => this.c.post(`/workflow/${id}/unpublish`, {});
  cloneWorkflow = (id: string) => this.c.post(`/workflow/${id}/clone`, {});
  getWorkflowVersions = (id: string) => this.c.get(`/workflow/${id}/versions`);
  getWorkflowInstances = (p?: any) => this.c.get('/workflow/instances', { params: p });
  getWorkflowInstance = (id: string) => this.c.get(`/workflow/instances/${id}`);
  getWorkflowTasks = (p?: any) => this.c.get('/workflow/tasks', { params: p });
  startWorkflowTask = (id: string) => this.c.put(`/workflow/tasks/${id}/start`, {});
  completeWorkflowTask = (id: string, d: any) => this.c.put(`/workflow/tasks/${id}/complete`, d);
  escalateWorkflowTask = (id: string, d: any) => this.c.put(`/workflow/tasks/${id}/escalate`, d);
  triggerWorkflow = (d: any) => this.c.post('/workflow/trigger', d);
  checkWorkflowSLA = () => this.c.post('/workflow/sla/check', {});
  getSLAReport = () => this.c.get('/workflow/sla-report');
  // Front Office Billing (CPOE → Billing → Department Authorization gate)
  getFOBDashboard = () => this.c.get('/front-office-billing/dashboard');
  getFOBQueue = (department?: string) => this.c.get('/front-office-billing/queue', { params: department?{department}:{} });
  getFOBOnHold = () => this.c.get('/front-office-billing/on-hold');
  billAndAuthorizeOrders = (d: any) => this.c.post('/front-office-billing/bill-and-authorize', d);
  putOrdersOnHold = (orderIds: string[], reason: string) => this.c.post('/front-office-billing/hold', { orderIds, reason });
  releaseOrdersHold = (orderIds: string[]) => this.c.post('/front-office-billing/release-hold', { orderIds });
  confirmBillPayment = (billId: string) => this.c.post(`/front-office-billing/confirm-payment/${billId}`, {});
  getAuthorizedPrescriptions = (patientId?: string) => this.c.get('/front-office-billing/prescriptions/authorized', { params: patientId?{patientId}:{} });
  getVisitBillSummary = (patientId: string, visitId?: string) => this.c.get(`/front-office-billing/visit-summary/${patientId}`, { params: visitId?{visitId}:{} });
  settleVisitBills = (d: any) => this.c.post('/front-office-billing/settle-visit', d);

  // Approval Workflow Engine
  getApprovalDashboard = () => this.c.get('/approval/dashboard');
  getApprovalChains = () => this.c.get('/approval/chains');
  getApprovalChain = (id: string) => this.c.get(`/approval/chains/${id}`);
  createApprovalChain = (d: any) => this.c.post('/approval/chains', d);
  updateApprovalChain = (id: string, d: any) => this.c.put(`/approval/chains/${id}`, d);
  setApprovalChainSteps = (id: string, steps: any[]) => this.c.put(`/approval/chains/${id}/steps`, { steps });
  getMyPendingApprovals = () => this.c.get('/approval/my-pending');
  getAllApprovalRequests = (status?: string, module?: string) => this.c.get('/approval/requests', { params: { status, module } });
  getApprovalRequestHistory = (id: string) => this.c.get(`/approval/requests/${id}/history`);
  actOnApproval = (id: string, action: 'APPROVED'|'REJECTED', comments?: string) => this.c.post(`/approval/requests/${id}/act`, { action, comments });
  // Approval Chain Engine
  // === DUTY ROSTER ===
  getRosterShifts = () => this.c.get('/roster/shifts');
  copyLastWeekRoster = (d: any) => this.c.post('/roster/copy-week', d);
  copyLastMonthRoster = (d: any) => this.c.post('/roster/copy-month', d);
  copyRoster = (d: any) => this.c.post('/roster/copy', d);
  deleteRosterEntry = (id: string) => this.c.delete(`/roster/entry/${id}`);
  createRosterShift = (d: any) => this.c.post('/roster/shifts', d);
  updateRosterShift = (id: string, d: any) => this.c.put(`/roster/shifts/${id}`, d);
  deleteRosterShift = (id: string) => this.c.delete(`/roster/shifts/${id}`);
  getHolidays = (year?: number) => this.c.get('/roster/holidays', { params: { year } });
  addHoliday = (d: any) => this.c.post('/roster/holidays', d);
  deleteHoliday = (id: string) => this.c.delete(`/roster/holidays/${id}`);
  checkHoliday = (date: string) => this.c.get(`/roster/holidays/check/${date}`);
  getRosterGrid = (p: any) => this.c.get('/roster/grid', { params: p });
  getRosterDashboard = (date?: string) => this.c.get('/roster/dashboard', { params: { date } });
  createRosterEntry = (d: any) => this.c.post('/roster/entry', d);
  updateRosterEntryStatus = (id: string, d: any) => this.c.put(`/roster/entry/${id}/status`, d);
  bulkAssignRoster = (d: any) => this.c.post('/roster/bulk-assign', d);
  getRosterAvailableSlots = (doctorId: string, date: string) => this.c.get('/roster/slots', { params: { doctorId, date } });
  generateSlots = (d: any) => this.c.post('/roster/slots/generate', d);
  getRosterAudit = (p?: any) => this.c.get('/roster/audit', { params: p });

  // === LAB PARAMETER MASTER ===
  getLabParameters = (p?: any) => this.c.get('/lab-master/parameters', { params: p });
  getLabParamCategories = () => this.c.get('/lab-master/parameters/categories');
  getLabParameter = (id: string) => this.c.get(`/lab-master/parameters/${id}`);
  getLabRanges = (parameterId: string) => this.c.get(`/lab-master/ranges/${parameterId}`);
  createLabRange = (d: any) => this.c.post('/lab-master/ranges', d);
  updateLabRange = (id: string, d: any) => this.c.put(`/lab-master/ranges/${id}`, d);
  deleteLabRange = (id: string) => this.c.delete(`/lab-master/ranges/${id}`);
  interpretLabResult = (d: any) => this.c.post('/lab-master/interpret', d);

  // === CORPORATE / INSURANCE ===
  getInsuranceProviders = (p?: any) => this.c.get('/corporate/providers', { params: p });
  getInsuranceProvider = (id: string) => this.c.get(`/corporate/providers/${id}`);
  createInsuranceProvider = (d: any) => this.c.post('/corporate/providers', d);
  updateInsuranceProvider = (id: string, d: any) => this.c.put(`/corporate/providers/${id}`, d);
  deleteInsuranceProvider = (id: string) => this.c.delete(`/corporate/providers/${id}`);
  getCorporateTariffs = (providerId: string, p?: any) => this.c.get(`/corporate/tariffs/${providerId}`, { params: p });
  createCorporateTariff = (d: any) => this.c.post('/corporate/tariffs', d);
  updateCorporateTariff = (id: string, d: any) => this.c.put(`/corporate/tariffs/${id}`, d);
  deleteCorporateTariff = (id: string) => this.c.delete(`/corporate/tariffs/${id}`);
  getCorporateServicePrice = (d: any) => this.c.post('/corporate/price', d);
  createPaymentReceipt = (d: any) => this.c.post('/corporate/receipts', d);
  getPaymentReceipt = (id: string) => this.c.get(`/corporate/receipts/${id}`);
  getPatientReceipts = (patientId: string) => this.c.get(`/corporate/receipts/patient/${patientId}`);
  cancelReceipt = (id: string) => this.c.put(`/corporate/receipts/${id}/cancel`, {});
  getCorporateLedger = (providerId: string, p: any) => this.c.get(`/corporate/ledger/${providerId}`, { params: p });

  // === MISSING METHODS ===
  getDoctorProfile = (doctorId: string) => this.c.get(`/scheduling/doctor-profile/${doctorId}`);

  // === SYSTEM MASTERS ===
  getMasters = () => this.c.get('/settings/masters');
  getMasterTypes = () => this.c.get('/settings/masters/types');
  getMasterByType = (type: string) => this.c.get(`/masters/${type}`);
  createMaster = (d: any) => this.c.post('/settings/masters', d);
  updateMaster = (id: string, d: any) => this.c.put(`/masters/${id}`, d);
  deleteMaster = (id: string) => this.c.delete(`/masters/${id}`);

  checkRosterConflict = (doctorId: string, date: string, shiftId: string) => this.c.get('/roster/conflict', { params: { doctorId, date, shiftId } });
  emergencyRosterOverride = (d: any) => this.c.post('/roster/emergency-override', d);
  markDoctorAbsent = (id: string, d: any) => this.c.put(`/roster/entry/${id}/absent`, d);
  getRosterSlotDetail = (doctorId: string, date: string) => this.c.get('/roster/slots/detail', { params: { doctorId, date } });

  // === ENTERPRISE ===
  getExecutiveDashboard = () => this.c.get('/enterprise/dashboard');
  getEnterpriseBedBoard = (wardId?: string) => this.c.get('/enterprise/bed-board', { params: wardId ? { wardId } : {} });
  getEnterpriseWards = () => this.c.get('/enterprise/wards');
  getSystemHealth = () => this.c.get('/health');
  getSystemHealthDetailed = () => this.c.get('/health/detailed');

  // Backup / DR
  getBackups = (p?: any) => this.c.get('/backup', { params: p });
  getBackup = (id: string) => this.c.get(`/backup/${id}`);
  getLastSuccessfulBackup = () => this.c.get('/backup/last-successful');
  triggerBackup = () => this.c.post('/backup', {});
  downloadBackup = (id: string) => this.c.get(`/backup/${id}/download`, { responseType: 'blob' });
  deleteBackup = (id: string) => this.c.delete(`/backup/${id}`);
  restoreBackup = (id: string, confirmationText: string) => this.c.post(`/backup/${id}/restore`, { confirmationText });
  getRestoreJobs = (p?: any) => this.c.get('/backup/restore-jobs', { params: p });
  getDrStatus = () => this.c.get('/backup/dr-status');

  // Advance Payments
  createAdvancePayment = (d: any) => this.c.post('/enterprise/advance-payments', d);
  getPatientAdvances = (patientId: string) => this.c.get(`/advance-payments/${patientId}`);

  // Package Billing
  assignBillingPackage = (d: any) => this.c.post('/enterprise/packages/assign', d);

  // Insurance Pre-Auth

  // Housekeeping
  requestHousekeeping = (d: any) => this.c.post('/enterprise/housekeeping/request', d);
  updateHousekeepingStatus = (id: string, d: any) => this.c.put(`/housekeeping/${id}`, d);
  getPendingHousekeeping = () => this.c.get('/enterprise/housekeeping/pending');

  // Credit Notes
  createCreditNote = (d: any) => this.c.post('/billing/credit-notes', d);
  approveCreditNote = (id: string) => this.c.put(`/credit-notes/${id}/approve`, {});
  getCreditNotes = (patientId?: string) => this.c.get('/billing/credit-notes', { params: patientId ? { patientId } : {} });

  // Quality Indicators
  recordQualityMeasurement = (d: any) => this.c.post('/quality/kpi', d);

  // CDS
  checkCdsAlerts = (patientId: string, drugName: string) => this.c.post('/emr/cds/check', { patientId, drugName });

  // Surgical Checklist
  createSurgicalChecklist = (d: any) => this.c.post('/ot/surgical-checklists', d);
  updateSurgicalChecklist = (id: string, d: any) => this.c.put(`/surgical-checklist/${id}`, d);

  // GST Invoice
  generateGstInvoice = (billId: string) => this.c.post(`/gst-invoice/${billId}`, {});

  // MIS Reports
  getMisReport = (from?: string, to?: string) => this.c.get('/reports/mis', { params: { from, to } });

  // Lab reference ranges
  getReferenceRanges = (parameterId: string) => this.c.get(`/lab-master/ranges/${parameterId}`);
  addReferenceRange = (d: any) => this.c.post('/lab-master/ranges', d);
  updateReferenceRange = (id: string, d: any) => this.c.put(`/lab-master/ranges/${id}`, d);
  deleteReferenceRange = (id: string) => this.c.delete(`/lab-master/ranges/${id}`);

  verifyCulture = (id: string) => this.c.put(`/laboratory/culture/${id}/verify`, {});
  getLabUnits = () => this.c.get('/laboratory/masters/units');
  getLabReportTemplates = (p?: any) => this.c.get('/laboratory/masters/templates', { params: p });
  getLabFormulas = () => this.c.get('/laboratory/masters/formulas');
  createLabOrganism = (d: any) => this.c.post('/laboratory/masters/organisms', d);
  createLabAntibiotic = (d: any) => this.c.post('/laboratory/masters/antibiotics', d);
  updateReportTemplate = (id: string, d: any) => this.c.put(`/laboratory/masters/templates/${id}`, d);
  interpretLabResults = (parameters: any[]) => this.c.post('/laboratory/results/interpret', { parameters });
  getLabResultHistory = (id: string) => this.c.get(`/laboratory/results/${id}/history`);
  signLabResult = (id: string, d: any) => this.c.post(`/laboratory/results/${id}/sign`, d);
  getLabOrders = (p?: any) => this.c.get('/laboratory/test-requests', { params: p });
  createLabParameter = (d: any) => this.c.post('/lab-master/parameters', d);
  updateLabParameter = (id: string, d: any) => this.c.put(`/lab-master/parameters/${id}`, d);
  deleteLabParameter = (id: string) => this.c.delete(`/lab-master/parameters/${id}`);

  getReceipts = (billId: string) => this.c.get(`/billing/${billId}/receipts`);

  recordPayment = (id: string, d: any) => this.c.post(`/billing/${id}/payment`, d);
  getBills = (p?: any) => this.c.get('/billing', { params: p });


  addAllergy = (d: any) => this.c.post(`/patients/${d.patientId}/allergies`, d);
  getAllergiesById = (patientId: string) => this.c.get(`/patients/${patientId}/allergies`);
  addDiagnosis = (d: any) => this.c.post('/emr/diagnoses', d);

  saveConsultation = (d: any) => this.c.post('/opd/consultations', d);

  getRadiologyResults = (patientId: string) => this.c.get('/radiology/reports', { params: { patientId } });

  getPharmacyOrders = (p?: any) => this.c.get('/pharmacy/orders', { params: p });

  dispenseOrder = (d: any) => this.c.post('/pharmacy/dispense', d);

  quickDispense = (d: any) => this.c.post('/pharmacy/quick-dispense', d);

  getDrugBatches = (drugId: string, method?: string) => this.c.get(`/pharmacy/batches/${drugId}`, { params: { method } });

  createOtcSale = (d: any) => this.c.post('/pharmacy/otc', d);

  getPendingProcedures = () => this.c.get('/nursing/procedures/pending/all');

  addNursingProcedure = (d: any) => this.c.post('/nursing/procedures', d);




  addVitals = (d: any) => this.c.post('/nursing/vitals', d);


  getPayroll = (p?: any) => this.c.get('/hrms/payroll', { params: p });

  getPayslip = (id: string) => this.c.get(`/hr/payroll/${id}/payslip`);

  getLeaveRequests = (p?: any) => this.c.get('/hrms/leaves', { params: p });

  rejectLeave = (id: string, d: any) => this.c.put(`/hr/leaves/${id}/reject`, d);

  getAttendance = (p?: any) => this.c.get('/hrms/attendance', { params: p });

  getEmployee = (id: string) => this.c.get(`/hr/employees/${id}`);

  getIncomeStatement = (p?: any) => this.c.get('/finance/income-statement', { params: p });

  getCashFlow = (p?: any) => this.c.get('/finance/cash-flow', { params: p });

  verifyConsentOTP = (id: string, d: any) => this.c.post(`/consents/${id}/verify-otp`, d);

  getOTSchedule = (p?: any) => this.c.get('/ot/schedule', { params: p });

  getOTCases = (p?: any) => this.c.get('/ot/cases', { params: p });

  updateOTStatus = (id: string, d: any) => this.c.put(`/ot/cases/${id}`, d);

  getBloodUnits = (p?: any) => this.c.get('/bloodbank/components', { params: p });

  requestBlood = (d: any) => this.c.post('/bloodbank/requests', d);

  crossMatch = (d: any) => this.c.post('/bloodbank/crossmatch', d);

  updateClaimStatus = (id: string, d: any) => this.c.put(`/insurance/claims/${id}`, d);

  getOphthalmExams = (p?: any) => this.c.get('/ophthalmology/examinations', { params: p });

  createOphthalmExam = (d: any) => this.c.post('/ophthalmology/examinations', d);

  getOphthalmExamFull = (id: string) => this.c.get(`/ophthalmology/examinations/${id}`);

  updateOphthalmExam = (id: string, d: any) => this.c.put(`/ophthalmology/examinations/${id}`, d);

  saveOphthalmVA = (d: any) => this.c.post('/ophthalmology/visual-acuity', d);

  saveOphthalmRefraction = (d: any) => this.c.post('/ophthalmology/refraction', d);

  saveOphthalmIop = (d: any) => this.c.post('/ophthalmology/iop', d);

  saveOphthalmSlitLamp = (d: any) => this.c.post('/ophthalmology/slit-lamp', d);

  saveOphthalmFundus = (d: any) => this.c.post('/ophthalmology/fundus', d);

  getOphthalmProcedures = (p?: any) => this.c.get('/ophthalmology/procedures', { params: p });

  createOphthalmProcedure = (d: any) => this.c.post('/ophthalmology/procedures', d);

  updateOphthalmProcedure = (id: string, d: any) => this.c.put(`/ophthalmology/procedures/${id}`, d);

  createOphthalmPrescription = (d: any) => this.c.post('/ophthalmology/prescriptions', d);
  getOphthalmPrescriptions = (patientId: string) => this.c.get(`/ophthalmology/prescriptions/${patientId}`);
  getOphthalmStats = () => this.c.get('/ophthalmology/stats');

  // ── Maternity & Newborn ─────────────────────────────────────────────
  getMaternityStats = () => this.c.get('/maternity/stats');
  getAncVisits = (patientId: string) => this.c.get(`/maternity/anc-visits/${patientId}`);
  createAncVisit = (d: any) => this.c.post('/maternity/anc-visits', d);
  updateAncVisit = (id: string, d: any) => this.c.put(`/maternity/anc-visits/${id}`, d);
  getDeliveries = (p?: any) => this.c.get('/maternity/deliveries', { params: p });
  createDelivery = (d: any) => this.c.post('/maternity/deliveries', d);
  getDeliveryFull = (id: string) => this.c.get(`/maternity/deliveries/${id}`);
  updateDelivery = (id: string, d: any) => this.c.put(`/maternity/deliveries/${id}`, d);
  addPartographEntry = (id: string, d: any) => this.c.post(`/maternity/deliveries/${id}/partograph`, d);
  createNewborn = (d: any) => this.c.post('/maternity/newborns', d);
  updateNewborn = (id: string, d: any) => this.c.put(`/maternity/newborns/${id}`, d);
  getNewbornsByMother = (patientId: string) => this.c.get(`/maternity/newborns/by-mother/${patientId}`);

  // ── Renal Care (Dialysis) ───────────────────────────────────────────
  getRenalStats = () => this.c.get('/renal-care/stats');
  getVascularAccesses = (patientId: string) => this.c.get(`/renal-care/vascular-access/${patientId}`);
  createVascularAccess = (d: any) => this.c.post('/renal-care/vascular-access', d);
  updateVascularAccess = (id: string, d: any) => this.c.put(`/renal-care/vascular-access/${id}`, d);
  getDialysisSessions = (p?: any) => this.c.get('/renal-care/sessions', { params: p });
  createDialysisSession = (d: any) => this.c.post('/renal-care/sessions', d);
  updateDialysisSession = (id: string, d: any) => this.c.put(`/renal-care/sessions/${id}`, d);
  getDialysisSessionCount = (patientId: string) => this.c.get(`/renal-care/sessions/count/${patientId}`);

  // ── Mental Wellness ──────────────────────────────────────────────────
  getMentalHealthStats = () => this.c.get('/mental-health/stats');
  getPsychAssessments = (p?: any) => this.c.get('/mental-health/assessments', { params: p });
  createPsychAssessment = (d: any) => this.c.post('/mental-health/assessments', d);
  getPsychAssessment = (id: string) => this.c.get(`/mental-health/assessments/${id}`);
  updatePsychAssessment = (id: string, d: any) => this.c.put(`/mental-health/assessments/${id}`, d);
  getCounselingSessions = (patientId: string) => this.c.get(`/mental-health/sessions/${patientId}`);
  createCounselingSession = (d: any) => this.c.post('/mental-health/sessions', d);
  saveMentalHealthPlan = (d: any) => this.c.post('/mental-health/care-plans', d);
  getMentalHealthPlan = (patientId: string) => this.c.get(`/mental-health/care-plans/${patientId}`);

  // ── Palliative Care ──────────────────────────────────────────────────
  getPalliativeStats = () => this.c.get('/palliative-care/stats');
  getPalliativeCases = (p?: any) => this.c.get('/palliative-care/cases', { params: p });
  createPalliativeCase = (d: any) => this.c.post('/palliative-care/cases', d);
  getPalliativeCaseFull = (id: string) => this.c.get(`/palliative-care/cases/${id}`);
  updatePalliativeCase = (id: string, d: any) => this.c.put(`/palliative-care/cases/${id}`, d);
  getPalliativeCaseByPatient = (patientId: string) => this.c.get(`/palliative-care/cases/by-patient/${patientId}`);
  createSymptomAssessment = (d: any) => this.c.post('/palliative-care/symptom-assessments', d);
  getSymptomTrend = (palliativeCaseId: string) => this.c.get(`/palliative-care/symptom-assessments/${palliativeCaseId}`);
  savePalliativePlan = (d: any) => this.c.post('/palliative-care/care-plans', d);

  uploadFile = (category: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return this.c.post(`/uploads/${category}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  };
  uploadFiles = (category: string, files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return this.c.post(`/uploads/${category}/batch`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  };


  // ── Patient Extended ──────────────────────────────────────────────────
  updatePatientProblem = (id: string, d: any) => this.c.put(`/patients/problems/${id}`, d);
  getPatientConditions = (pid: string) => this.c.get(`/patients/${pid}/conditions`);
  addPatientCondition = (pid: string, d: any) => this.c.post(`/patients/${pid}/conditions`, d);
  getPatientRiskFlags = (pid: string) => this.c.get(`/patients/${pid}/risk-flags`);
  addPatientRiskFlag = (pid: string, d: any) => this.c.post(`/patients/${pid}/risk-flags`, d);
  resolveRiskFlag = (id: string) => this.c.put(`/patients/risk-flags/${id}/resolve`, {});
  getPatientEmergencyContacts = (pid: string) => this.c.get(`/patients/${pid}/emergency-contacts`);
  addPatientEmergencyContact = (pid: string, d: any) => this.c.post(`/patients/${pid}/emergency-contacts`, d);
  getPatientTimeline = (pid: string) => this.c.get(`/patients/${pid}/timeline`);
  addTimelineEvent = (pid: string, d: any) => this.c.post(`/patients/${pid}/timeline`, d);
  getPatientGuardians = (pid: string) => this.c.get(`/patients/${pid}/guardians`);
  addPatientGuardian = (pid: string, d: any) => this.c.post(`/patients/${pid}/guardians`, d);

  // ── Nursing Assessments ───────────────────────────────────────────────
  getPainAssessments = (pid: string, aid?: string) => this.c.get(`/nursing/pain/${pid}`, { params: aid ? { admissionId: aid } : {} });
  addPainAssessment = (d: any) => this.c.post('/nursing/pain', d);
  getFallAssessments = (pid: string) => this.c.get(`/nursing/fall/${pid}`);
  addFallAssessment = (d: any) => this.c.post('/nursing/fall', d);
  getGlasgowScores = (pid: string) => this.c.get(`/nursing/glasgow/${pid}`);
  addGlasgowScore = (d: any) => this.c.post('/nursing/glasgow', d);
  getBradenScores = (pid: string) => this.c.get(`/nursing/braden/${pid}`);
  addBradenScore = (d: any) => this.c.post('/nursing/braden', d);
  getTaskMaster = () => this.c.get('/nursing/task-master');
  createNursingTask = (d: any) => this.c.post('/nursing/task-master', d);
  getTaskExecutions = (pid: string, aid?: string) => this.c.get(`/nursing/tasks/${pid}`, { params: aid ? { admissionId: aid } : {} });
  assignNursingTask = (d: any) => this.c.post('/nursing/tasks', d);
  completeNursingTask = (id: string, d: any) => this.c.put(`/nursing/tasks/${id}/complete`, d);
  getNursingRounds = (wid?: string) => this.c.get('/nursing/rounds', { params: wid ? { wardId: wid } : {} });
  addNursingRound = (d: any) => this.c.post('/nursing/rounds', d);

  // ── Lab Extended ──────────────────────────────────────────────────────
  getSampleTracking = (rid: string) => this.c.get(`/laboratory/sample/${rid}/tracking`);
  rejectSample = (rid: string, d: any) => this.c.post(`/laboratory/sample/${rid}/reject`, d);
  getResultComments = (rid: string) => this.c.get(`/laboratory/results/${rid}/comments`);
  addResultComment = (rid: string, d: any) => this.c.post(`/laboratory/results/${rid}/comments`, d);
  getTATRules = () => this.c.get('/laboratory/tat-rules');
  setTATRule = (d: any) => this.c.post('/laboratory/tat-rules', d);
  getOutsourceLabs = () => this.c.get('/laboratory/outsource-labs');
  createOutsourceOrder = (d: any) => this.c.post('/laboratory/outsource-orders', d);
  getOutsourceOrders = (status?: string) => this.c.get('/laboratory/outsource-orders', { params: status ? { status } : {} });
  getReagents = () => this.c.get('/laboratory/reagents');
  updateReagentStock = (id: string, qty: number) => this.c.put(`/laboratory/reagents/${id}/stock`, { quantity: qty });

  // ── Pharmacy Extended ─────────────────────────────────────────────────
  getDrugInteractions = (id: string, id2?: string) => this.c.get(`/pharmacy/drug-interactions/${id}`, { params: id2 ? { drugId2: id2 } : {} });
  checkDrugInteractions = (drugIds: string[]) => this.c.post('/pharmacy/check-interactions', { drugIds });
  addDrugInteraction = (d: any) => this.c.post('/pharmacy/drug-interactions', d);
  getDrugSubstitutes = (id: string) => this.c.get(`/pharmacy/substitutes/${id}`);
  addDrugSubstitute = (d: any) => this.c.post('/pharmacy/substitutes', d);
  getDrugPriceHistory = (id: string) => this.c.get(`/pharmacy/price-history/${id}`);
  addDrugPrice = (id: string, d: any) => this.c.post(`/pharmacy/price-history/${id}`, d);
  getDiscountRules = () => this.c.get('/pharmacy/discount-rules');
  createDiscountRule = (d: any) => this.c.post('/pharmacy/discount-rules', d);
  getExpiryAlerts = (branchId?: string) => this.c.get('/pharmacy/expiry-alerts', { params: branchId ? { branchId } : {} });
  refreshExpiryAlerts = () => this.c.post('/pharmacy/expiry-alerts/refresh', {});

  // ── OT Extended ───────────────────────────────────────────────────────
  getOTCheckin = (sid: string) => this.c.get(`/ot/checkin/${sid}`);
  doOTCheckin = (d: any) => this.c.post('/ot/checkin', d);
  doOTCheckout = (d: any) => this.c.post('/ot/checkout', d);
  getOTComplications = (sid: string) => this.c.get(`/ot/complications/${sid}`);
  addOTComplication = (d: any) => this.c.post('/ot/complications', d);
  getOTImplants = () => this.c.get('/ot/implants');
  createOTImplant = (d: any) => this.c.post('/ot/implants', d);
  addImplantInventory = (d: any) => this.c.post('/ot/implant-inventory', d);
  getAnesthesiaEvents = (sid: string) => this.c.get(`/ot/anesthesia/${sid}`);
  addAnesthesiaEvent = (d: any) => this.c.post('/ot/anesthesia', d);
  getOTDelays = (sid: string) => this.c.get(`/ot/delays/${sid}`);
  addOTDelay = (d: any) => this.c.post('/ot/delays', d);

  // ── Radiology Extended ────────────────────────────────────────────────
  getRadiologyWorklist = (machineId?: string, status?: string) => this.c.get('/radiology/worklist', { params: { machineId, status } });
  addToWorklist = (d: any) => this.c.post('/radiology/worklist', d);
  updateWorklistStatus = (id: string, status: string) => this.c.put(`/radiology/worklist/${id}/status`, { status });
  getRadiologyFindings = (oid: string) => this.c.get(`/radiology/findings/${oid}`);
  addRadiologyFinding = (d: any) => this.c.post('/radiology/findings', d);
  getRadiologyImpressions = (oid: string) => this.c.get(`/radiology/impressions/${oid}`);
  addRadiologyImpression = (d: any) => this.c.post('/radiology/impressions', d);
  getRadiologyMachines = (branchId?: string) => this.c.get('/radiology/machines', { params: branchId ? { branchId } : {} });
  getAllRadiologyMachines = (branchId?: string) => this.c.get('/radiology/machines/all', { params: branchId ? { branchId } : {} });
  createRadiologyMachine = (d: any) => this.c.post('/radiology/machines', d);
  updateRadiologyMachine = (id: string, d: any) => this.c.put(`/radiology/machines/${id}`, d);
  deactivateRadiologyMachine = (id: string) => this.c.put(`/radiology/machines/${id}/deactivate`, {});
  getAllRadiologyRooms = () => this.c.get('/radiology/rooms/all');
  createRadiologyRoom = (d: any) => this.c.post('/radiology/rooms', d);
  updateRadiologyRoom = (id: string, d: any) => this.c.put(`/radiology/rooms/${id}`, d);
  deactivateRadiologyRoom = (id: string) => this.c.put(`/radiology/rooms/${id}/deactivate`, {});
  getRadiologyModalities = () => this.c.get('/radiology/modalities');
  getAllRadiologyModalities = () => this.c.get('/radiology/modalities/all');
  createRadiologyModality = (d: any) => this.c.post('/radiology/modalities', d);
  updateRadiologyModality = (id: string, d: any) => this.c.put(`/radiology/modalities/${id}`, d);
  getRadiologyMeasurements = (oid: string) => this.c.get(`/radiology/measurements/${oid}`);
  addRadiologyMeasurement = (d: any) => this.c.post('/radiology/measurements', d);

  // ── Appointment Extended ──────────────────────────────────────────────
  getAppointmentToken = (id: string) => this.c.get(`/appointments/${id}/token`);
  generateAppointmentToken = (id: string) => this.c.post(`/appointments/${id}/token/generate`, {});
  updateTokenStatus = (id: string, status: string) => this.c.put(`/appointments/${id}/token/status`, { status });
  getDoctorQueue = (doctorId: string, date?: string) => this.c.get(`/appointments/queue/${doctorId}`, { params: date ? { date } : {} });
  addToQueue = (d: any) => this.c.post('/appointments/queue', d);
  callNextPatient = (doctorId: string) => this.c.post('/appointments/queue/call-next', { doctorId });
  getWaitlist = (doctorId?: string) => this.c.get('/appointments/waitlist', { params: doctorId ? { doctorId } : {} });
  addToWaitlist = (d: any) => this.c.post('/appointments/waitlist', d);

  // ── Settings Extended ─────────────────────────────────────────────────
  getAppSettings = (category?: string) => this.c.get('/settings/app-settings', { params: category ? { category } : {} });
  updateAppSetting = (key: string, value: string) => this.c.put(`/settings/app-settings/${key}`, { value });
  bulkUpdateAppSettings = (settings: any[]) => this.c.post('/settings/app-settings/bulk', { settings });
  getFeatureFlags = () => this.c.get('/settings/feature-flags');
  toggleFeatureFlag = (code: string, enabled: boolean) => this.c.put(`/settings/feature-flags/${code}`, { enabled });
  getUserPreferences = () => this.c.get('/settings/preferences');
  updateUserPreferences = (d: any) => this.c.put('/settings/preferences', d);
  getBranding = () => this.c.get('/settings/branding');
  updateBranding = (d: any) => this.c.put('/settings/branding', d);
  queueSMS = (d: any) => this.c.post('/settings/notify/sms', d);
  queueEmail = (d: any) => this.c.post('/settings/notify/email', d);
  queueWhatsApp = (d: any) => this.c.post('/settings/notify/whatsapp', d);
  getSMSQueue = (status?: string) => this.c.get('/settings/notify/sms-queue', { params: status ? { status } : {} });
  getEmailQueue = (status?: string) => this.c.get('/settings/notify/email-queue', { params: status ? { status } : {} });


  // ── Blood Bank Extended ───────────────────────────────────────────────
  getBloodScreenings = (donationId: string) => this.c.get(`/bloodbank/screening/${donationId}`);
  addBloodScreening = (d: any) => this.c.post('/bloodbank/screening', d);
  prepareBloodComponent = (d: any) => this.c.post('/bloodbank/components', d);
  testBloodComponent = (id: string, d: any) => this.c.post(`/bloodbank/components/${id}/test`, d);
  quarantineComponent = (id: string, d: any) => this.c.post(`/bloodbank/components/${id}/quarantine`, d);
  releaseFromQuarantine = (id: string) => this.c.put(`/bloodbank/components/${id}/release`, {});
  getTemperatureLogs = (unit?: string) => this.c.get('/bloodbank/temperature-logs', { params: unit ? { unit } : {} });
  addTemperatureLog = (d: any) => this.c.post('/bloodbank/temperature-logs', d);
  transportBloodComponent = (d: any) => this.c.post('/bloodbank/transport', d);
  confirmBloodReceived = (id: string) => this.c.put(`/bloodbank/transport/${id}/received`, {});

  // ── Quality Module ────────────────────────────────────────────────────
  updateIncidentStatus = (id: string, status: string) => this.c.put(`/quality/incidents/${id}/status`, { status });
  getIncidentRCA = (id: string) => this.c.get(`/quality/incidents/${id}/rca`);
  addIncidentRCA = (id: string, d: any) => this.c.post(`/quality/incidents/${id}/rca`, d);
  getIncidentActions = (id: string) => this.c.get(`/quality/incidents/${id}/actions`);
  addIncidentAction = (id: string, d: any) => this.c.post(`/quality/incidents/${id}/actions`, d);
  completeAction = (id: string, d: any) => this.c.put(`/quality/actions/${id}/complete`, d);
  getQualityAudits = () => this.c.get('/quality/quality-audits');
  createQualityAudit = (d: any) => this.c.post('/quality/quality-audits', d);
  getDocumentControl = (status?: string) => this.c.get('/quality/document-control', { params: status ? { status } : {} });
  createQualityDocument = (d: any) => this.c.post('/quality/document-control', d);

  // ── HRMS Extended ─────────────────────────────────────────────────────
  getEmployeeSkills = (id: string) => this.c.get(`/hrms/employees/${id}/skills`);
  addEmployeeSkill = (id: string, d: any) => this.c.post(`/hrms/employees/${id}/skills`, d);
  getEmployeeCertifications = (id: string) => this.c.get(`/hrms/employees/${id}/certifications`);
  addEmployeeCertification = (id: string, d: any) => this.c.post(`/hrms/employees/${id}/certifications`, d);
  getEmployeeLicenses = (id: string) => this.c.get(`/hrms/employees/${id}/licenses`);
  addEmployeeLicense = (id: string, d: any) => this.c.post(`/hrms/employees/${id}/licenses`, d);
  getEmployeeTraining = (id: string) => this.c.get(`/hrms/employees/${id}/training`);
  addEmployeeTraining = (id: string, d: any) => this.c.post(`/hrms/employees/${id}/training`, d);
  getEmployeeAppraisals = (id: string) => this.c.get(`/hrms/employees/${id}/appraisals`);
  addEmployeeAppraisal = (id: string, d: any) => this.c.post(`/hrms/employees/${id}/appraisals`, d);
  getTransfers = (employeeId?: string) => this.c.get('/hrms/transfers', { params: employeeId ? { employeeId } : {} });
  createTransfer = (d: any) => this.c.post('/hrms/transfers', d);

  // ── Enterprise / Infection / CSSD / KPI ──────────────────────────────
  getInfectionCases = (p?: any) => this.c.get('/enterprise/infection/cases', { params: p });
  addInfectionCase = (d: any) => this.c.post('/enterprise/infection/cases', d);
  getInfectionSurveillance = (year?: number) => this.c.get('/enterprise/infection/surveillance', { params: year ? { year } : {} });
  getInfectionOutbreaks = () => this.c.get('/enterprise/infection/outbreaks');
  getHousekeepingTasks = (status?: string) => this.c.get('/enterprise/housekeeping', { params: status ? { status } : {} });
  createHousekeepingTask = (d: any) => this.c.post('/enterprise/housekeeping', d);
  completeHousekeepingTask = (id: string) => this.c.put(`/enterprise/housekeeping/${id}/complete`, {});
  getCSSDTrays = (status?: string) => this.c.get('/enterprise/cssd/trays', { params: status ? { status } : {} });
  trackCSSDEvent = (d: any) => this.c.post('/enterprise/cssd/track', d);
  dispatchCSSD = (d: any) => this.c.post('/enterprise/cssd/dispatch', d);
  getStores = (branchId?: string) => this.c.get('/enterprise/stores', { params: branchId ? { branchId } : {} });
  createStore = (d: any) => this.c.post('/enterprise/stores', d);
  getEnterpriseExpiryAlerts = () => this.c.get('/enterprise/expiry-alerts');
  getKPIs = () => this.c.get('/enterprise/kpis');
  recordKPI = (d: any) => this.c.post('/enterprise/kpis/record', d);
  getIntegrationLogs = (p?: any) => this.c.get('/enterprise/integration-logs', { params: p });
  logIntegration = (d: any) => this.c.post('/enterprise/integration-logs', d);

  // ── Finance Extended ──────────────────────────────────────────────────
  getInvoiceSeries = (branchId?: string) => this.c.get('/billing/invoice-series', { params: branchId ? { branchId } : {} });
  getPaymentAllocations = (invoiceId: string) => this.c.get(`/billing/payment-allocations/${invoiceId}`);
  createPaymentAllocation = (d: any) => this.c.post('/billing/payment-allocations', d);
  getGatewayTransactions = (paymentId: string) => this.c.get(`/billing/gateway-transactions/${paymentId}`);


  // ── HL7 ───────────────────────────────────────────────────────────────
  getHL7Messages = (p?: any) => this.c.get('/hl7/messages', { params: p });
  createHL7Message = (d: any) => this.c.post('/hl7/messages', d);
  processHL7Message = (id: string) => this.c.put(`/hl7/messages/${id}/process`, {});
  getHL7Queue = (status?: string) => this.c.get('/hl7/queue', { params: status ? { status } : {} });
  addHL7Ack = (id: string, d: any) => this.c.post(`/hl7/messages/${id}/ack`, d);

  // ── Audit Extended ────────────────────────────────────────────────────
  getAuditConfig = () => this.c.get('/audit-logs/config');
  setAuditConfig = (table: string, d: any) => this.c.put(`/audit/config/${table}`, d);
  getAuditArchives = () => this.c.get('/audit-logs/archives');
  createAuditArchive = (d: any) => this.c.post('/audit-logs/archives', d);
  getRetentionPolicies = () => this.c.get('/audit-logs/retention');
  setRetentionPolicy = (module: string, d: any) => this.c.put(`/audit/retention/${module}`, d);

  // ── Patient Extended ──────────────────────────────────────────────────
  getPatientAliases = (id: string) => this.c.get(`/patients/${id}/aliases`);
  addPatientAlias = (id: string, d: any) => this.c.post(`/patients/${id}/aliases`, d);
  getPatientDemographics = (id: string) => this.c.get(`/patients/${id}/demographics`);
  updatePatientDemographics = (id: string, d: any) => this.c.put(`/patients/${id}/demographics`, d);
  getPatientIdentifiers = (id: string) => this.c.get(`/patients/${id}/identifiers`);
  addPatientIdentifier = (id: string, d: any) => this.c.post(`/patients/${id}/identifiers`, d);
  updateCommPrefs = (id: string, d: any) => this.c.put(`/patients/${id}/comm-prefs`, d);
  getPatientPhotos = (id: string) => this.c.get(`/patients/${id}/photos`);
  addPatientPhoto = (id: string, d: any) => this.c.post(`/patients/${id}/photos`, d);
  getDuplicateLogs = (status?: string) => this.c.get('/patients/duplicate-logs', { params: status ? { status } : {} });

  // ── EMR Extended ──────────────────────────────────────────────────────
  getCareTeam = (pid: string, aid?: string) => this.c.get(`/emr/care-team/${pid}`, { params: aid ? { admissionId: aid } : {} });
  createCareTeam = (d: any) => this.c.post('/emr/care-team', d);
  addCareTeamMember = (id: string, d: any) => this.c.post(`/emr/care-team/${id}/members`, d);
  getClinicalPathways = (code?: string) => this.c.get('/emr/pathways', { params: code ? { diagnosisCode: code } : {} });
  createClinicalPathway = (d: any) => this.c.post('/emr/pathways', d);
  getClinicalGoals = (pid: string) => this.c.get(`/emr/goals/${pid}`);
  addClinicalGoal = (d: any) => this.c.post('/emr/goals', d);
  getClinicalRiskAssessments = (pid: string) => this.c.get(`/emr/risk-assessments/${pid}`);
  addClinicalRiskAssessment = (d: any) => this.c.post('/emr/risk-assessments', d);
  getClinicalForms = () => this.c.get('/emr/forms');
  getClinicalTemplates = (type?: string) => this.c.get('/emr/templates', { params: type ? { type } : {} });
  createClinicalTemplate = (d: any) => this.c.post('/emr/templates', d);
  getOrderCatalog = (department?: string) => this.c.get('/emr/order-catalog', { params: department ? { department } : {} });
  getOrderFavorites = () => this.c.get('/emr/order-favorites');
  addOrderFavorite = (d: any) => this.c.post('/emr/order-favorites', d);
  getOrderProtocols = (code?: string) => this.c.get('/emr/order-protocols', { params: code ? { diagnosisCode: code } : {} });

  // ── Finance Extended ──────────────────────────────────────────────────
  getCostCenters = (branchId?: string) => this.c.get('/finance/cost-centers', { params: branchId ? { branchId } : {} });
  createCostCenter = (d: any) => this.c.post('/finance/cost-centers', d);
  getProfitCenters = (branchId?: string) => this.c.get('/finance/profit-centers', { params: branchId ? { branchId } : {} });
  getFinanceAssets = () => this.c.get('/finance/assets');
  createFinanceAsset = (d: any) => this.c.post('/finance/assets', d);
  disposeAsset = (id: string, d: any) => this.c.post(`/finance/assets/${id}/dispose`, d);
  transferAsset = (id: string, d: any) => this.c.post(`/finance/assets/${id}/transfer`, d);
  getBudgetHistory = (p?: any) => this.c.get('/finance/budget-history', { params: p });
  recordBudget = (d: any) => this.c.post('/finance/budget-history', d);
  getFinanceInvoiceSeries = (branchId?: string) => this.c.get('/finance/invoice-series', { params: branchId ? { branchId } : {} });
  getNextInvoiceNumber = (branchId: string) => this.c.get(`/finance/invoice-series/next/${branchId}`);

  // ── Branch Config ─────────────────────────────────────────────────────
  getNumberSeries = (branchId: string) => this.c.get(`/settings/number-series/${branchId}`);
  updateNumberSeries = (branchId: string, type: string, d: any) => this.c.put(`/settings/number-series/${branchId}/${type}`, d);
  getBranchPrinters = (branchId: string) => this.c.get(`/settings/printers/${branchId}`);
  addBranchPrinter = (d: any) => this.c.post('/settings/printers', d);
  getBranchEmailConfig = (branchId: string) => this.c.get(`/settings/email-config/${branchId}`);
  updateBranchEmailConfig = (branchId: string, d: any) => this.c.put(`/settings/email-config/${branchId}`, d);
  getBranchSMSConfig = (branchId: string) => this.c.get(`/settings/sms-config/${branchId}`);
  updateBranchSMSConfig = (branchId: string, d: any) => this.c.put(`/settings/sms-config/${branchId}`, d);
  getBranchTaxConfig = (branchId: string) => this.c.get(`/settings/tax-config/${branchId}`);
  addBranchTaxConfig = (d: any) => this.c.post('/settings/tax-config', d);
  getBranchHolidays = (branchId: string) => this.c.get(`/settings/holidays/${branchId}`);
  addBranchHoliday = (d: any) => this.c.post('/settings/holidays', d);
  getBranchWeekoffs = (branchId: string) => this.c.get(`/settings/weekoffs/${branchId}`);
  setBranchWeekoffs = (branchId: string, days: number[]) => this.c.put(`/settings/weekoffs/${branchId}`, { days });

  // ── Drug Master ───────────────────────────────────────────────────────
  getDrugForms = () => this.c.get('/pharmacy/drug-forms');
  getDrugRoutes = () => this.c.get('/pharmacy/drug-routes');
  getDrugClasses = () => this.c.get('/pharmacy/drug-classes');
  getDrugGenerics = (classId?: string) => this.c.get('/pharmacy/drug-generics', { params: classId ? { classId } : {} });
  createDrugGeneric = (d: any) => this.c.post('/pharmacy/drug-generics', d);
  getDrugContraindications = (id: string) => this.c.get(`/pharmacy/contraindications/${id}`);
  getDrugStorageConditions = (id: string) => this.c.get(`/pharmacy/storage/${id}`);
  setDrugStorageConditions = (id: string, d: any) => this.c.put(`/pharmacy/storage/${id}`, d);
  getDrugRecalls = () => this.c.get('/pharmacy/recalls');
  addDrugRecall = (d: any) => this.c.post('/pharmacy/recalls', d);
  seedDrugMasterData = () => this.c.post('/pharmacy/seed-master-data', {});


  // ── Organization ──────────────────────────────────────────────────────
  getOrganization = () => this.c.get('/settings/organization');
  updateOrganization = (d: any) => this.c.put('/settings/organization', d);
  getLocalization = () => this.c.get('/settings/localization');
  updateLocalization = (d: any) => this.c.put('/settings/localization', d);
  getCountries = () => this.c.get('/settings/countries');
  getOrgFeatures = () => this.c.get('/settings/org-features');
  toggleOrgFeature = (code: string, enabled: boolean) => this.c.put(`/settings/org-features/${code}`, { enabled });

  // ── Database-content translations (separate from i18next UI-chrome translations) ──────────
  getTranslations = (entityType: string) => this.c.get('/translations', { params: { entityType } });
  upsertTranslation = (d: { entityType: string; entityId: string; fieldName: string; languageCode: string; value: string }) => this.c.put('/translations', d);

  // ── On-demand note translation (backend/src/translate/translate.module.ts) — LibreTranslate,
  // not a paid AI service. OPTIONAL on the server: if unconfigured, this rejects with a 503
  // that callers (see components/common/TranslateButton.tsx) treat as "feature unavailable".
  translateText = (text: string, target: string, source?: string) => this.c.post('/translate', { text, target, source });
  // Cached auto-translation for reference/catalog data (drug/lab/master-data names etc.) —
  // see backend POST /translate/auto. Safe to call per-row in a list; repeat calls for the
  // same text+language hit the server-side DB cache instead of LibreTranslate.
  translateAuto = (text: string, target: string, source?: string) => this.c.post('/translate/auto', { text, target, source });
  getOrgLicenses = () => this.c.get('/settings/org-licenses');
  addOrgLicense = (d: any) => this.c.post('/settings/org-licenses', d);
  getOrgContacts = () => this.c.get('/settings/org-contacts');
  addOrgContact = (d: any) => this.c.post('/settings/org-contacts', d);

  // ── Workflow Extended ─────────────────────────────────────────────────
  getWorkflowConditions = (id: string) => this.c.get(`/workflow/${id}/conditions`);
  addWorkflowCondition = (id: string, d: any) => this.c.post(`/workflow/${id}/conditions`, d);
  getWorkflowEvents = (id: string) => this.c.get(`/workflow/${id}/events`);
  getWorkflowEscalations = (id: string) => this.c.get(`/workflow/${id}/escalations`);
  addWorkflowEscalation = (id: string, d: any) => this.c.post(`/workflow/${id}/escalations`, d);
  getMyWorkflowNotifications = (unread?: boolean) => this.c.get('/workflow/notifications/mine', { params: unread ? { unread: 'true' } : {} });
  getWorkflowAssignments = (userId?: string) => this.c.get('/workflow/assignments', { params: userId ? { userId } : {} });
  createWorkflowAssignment = (d: any) => this.c.post('/workflow/assignments', d);
  getWorkflowHistory = (instanceId: string) => this.c.get(`/workflow/instances/${instanceId}/history`);

  // ── Biomedical Extended ───────────────────────────────────────────────
  getEquipmentWarranties = (eid?: string) => this.c.get('/biomedical/warranties', { params: eid ? { equipmentId: eid } : {} });
  addEquipmentWarranty = (d: any) => this.c.post('/biomedical/warranties', d);
  getEquipmentParts = (eid: string) => this.c.get(`/biomedical/parts/${eid}`);
  addEquipmentPart = (d: any) => this.c.post('/biomedical/parts', d);
  getEquipmentServiceHistory = (eid: string) => this.c.get(`/biomedical/service-history/${eid}`);
  addEquipmentService = (d: any) => this.c.post('/biomedical/service-history', d);
  getEquipmentIncidents = (eid?: string) => this.c.get('/biomedical/incidents', { params: eid ? { equipmentId: eid } : {} });
  reportEquipmentIncident = (d: any) => this.c.post('/biomedical/incidents', d);
  resolveEquipmentIncident = (id: string, d: any) => this.c.put(`/biomedical/incidents/${id}/resolve`, d);
  logEquipmentUtilization = (d: any) => this.c.post('/biomedical/utilization', d);

  // ── Lab Extended ──────────────────────────────────────────────────────
  getLabAnalyzers = (sectionId?: string) => this.c.get('/laboratory/analyzers', { params: sectionId ? { sectionId } : {} });
  createLabAnalyzer = (d: any) => this.c.post('/laboratory/analyzers', d);
  getLabQCRules = (analyzerId?: string) => this.c.get('/laboratory/qc-rules', { params: analyzerId ? { analyzerId } : {} });
  addLabQCRule = (d: any) => this.c.post('/laboratory/qc-rules', d);
  getDeltaCheckRules = () => this.c.get('/laboratory/delta-check-rules');
  addDeltaCheckRule = (d: any) => this.c.post('/laboratory/delta-check-rules', d);
  getLabDepartments = (branchId?: string) => this.c.get('/laboratory/departments', { params: branchId ? { branchId } : {} });
  getLabSections = (deptId?: string) => this.c.get('/laboratory/sections', { params: deptId ? { deptId } : {} });
  createLabSection = (d: any) => this.c.post('/laboratory/sections', d);
  getLabCalibrations = (analyzerId?: string) => this.c.get('/laboratory/calibrations', { params: analyzerId ? { analyzerId } : {} });
  scheduleCalibration = (d: any) => this.c.post('/laboratory/calibrations', d);
  recordCalibrationResults = (id: string, d: any) => this.c.post(`/laboratory/calibrations/${id}/results`, d);
  getLabConsumables = () => this.c.get('/laboratory/consumables');
  updateConsumableStock = (id: string, qty: number) => this.c.put(`/laboratory/consumables/${id}/stock`, { quantity: qty });

  // ── Diet Extended ─────────────────────────────────────────────────────
  getMealTemplates = (dietType?: string) => this.c.get('/diet/meal-templates', { params: dietType ? { dietType } : {} });
  createMealTemplate = (d: any) => this.c.post('/diet/meal-templates', d);
  getMealItems = (templateId: string) => this.c.get(`/diet/meal-items/${templateId}`);
  addMealItem = (d: any) => this.c.post('/diet/meal-items', d);
  getMealDelivery = (pid: string, aid?: string) => this.c.get(`/diet/delivery/${pid}`, { params: aid ? { admissionId: aid } : {} });
  recordMealDelivery = (d: any) => this.c.post('/diet/delivery', d);
  getDieticianNotes = (pid: string) => this.c.get(`/diet/notes/${pid}`);
  addDieticianNote = (d: any) => this.c.post('/diet/notes', d);
  getKitchenInventory = () => this.c.get('/diet/kitchen-inventory');
  updateKitchenStock = (id: string, qty: number) => this.c.put(`/diet/kitchen-inventory/${id}/stock`, { quantity: qty });

  // ── Emergency Extended ────────────────────────────────────────────────
  getTriageCategories = () => this.c.get('/emergency/triage/categories');
  getTriageHistory = (pid?: string) => this.c.get('/emergency/triage/history', { params: pid ? { patientId: pid } : {} });
  addTriageEntry = (d: any) => this.c.post('/emergency/triage', d);
  getAmbulanceDispatches = (status?: string) => this.c.get('/emergency/ambulance', { params: status ? { status } : {} });
  trackAmbulance = (id: string, d: any) => this.c.post(`/emergency/ambulance/${id}/track`, d);
  getCodeBlueEvents = () => this.c.get('/emergency/code-blue');
  createCodeBlue = (d: any) => this.c.post('/emergency/code-blue', d);
  updateCodeBlue = (id: string, d: any) => this.c.put(`/emergency/code-blue/${id}`, d);
  getTraumaRegistry = () => this.c.get('/emergency/trauma-registry');
  registerTrauma = (d: any) => this.c.post('/emergency/trauma-registry', d);

  // ── FHIR Extended ─────────────────────────────────────────────────────
  storeFHIRResource = (d: any) => this.c.post('/fhir/resources', d);
  getFHIRResource = (type: string, id: string) => this.c.get(`/fhir/resources/${type}/${id}`);
  getPatientFHIRResources = (pid: string) => this.c.get(`/fhir/resources/patient/${pid}`);
  storeFHIRBundle = (d: any) => this.c.post('/fhir/r4/Bundle', d);
  getPatientFHIRBundles = (pid: string) => this.c.get(`/fhir/bundles/${pid}`);
  markFHIRBundlePushed = (id: string) => this.c.put(`/fhir/bundles/${id}/push`, {});
  getFHIRSubscriptions = () => this.c.get('/fhir/subscriptions');
  addFHIRSubscription = (d: any) => this.c.post('/fhir/subscriptions', d);

  // ── Inventory Extended ────────────────────────────────────────────────
  getRacks = (storeId?: string) => this.c.get('/inventory/racks', { params: storeId ? { storeId } : {} });
  getBinLocations = (rackId?: string) => this.c.get('/inventory/bin-locations', { params: rackId ? { rackId } : {} });
  addBinLocation = (d: any) => this.c.post('/inventory/bin-locations', d);
  getStockReservations = (itemId?: string) => this.c.get('/inventory/reservations', { params: itemId ? { itemId } : {} });
  reserveStock = (d: any) => this.c.post('/inventory/reservations', d);
  releaseReservation = (id: string) => this.c.put(`/inventory/reservations/${id}/release`, {});
  getCycleCounts = (storeId?: string) => this.c.get('/inventory/cycle-counts', { params: storeId ? { storeId } : {} });
  startCycleCount = (d: any) => this.c.post('/inventory/cycle-counts', d);
  addReconciliationItem = (id: string, d: any) => this.c.post(`/inventory/cycle-counts/${id}/items`, d);
  getStockLotHistory = (itemId: string) => this.c.get(`/inventory/lot-history/${itemId}`);
  getInventoryValuations = (storeId?: string) => this.c.get('/inventory/valuations', { params: storeId ? { storeId } : {} });

  // ── HL7 ───────────────────────────────────────────────────────────────


  // ── Insurance Extended ────────────────────────────────────────────────
  getClaimAudit = (id: string) => this.c.get(`/insurance/claims/${id}/audit`);
  addClaimAudit = (id: string, d: any) => this.c.post(`/insurance/claims/${id}/audit`, d);
  getClaimStatusHistory = (id: string) => this.c.get(`/insurance/claims/${id}/status-history`);
  getAuthorizationHistory = (id: string) => this.c.get(`/insurance/preauth/${id}/history`);

  // ── Microbiology Extended ─────────────────────────────────────────────
  getCultureMedia = () => this.c.get('/laboratory/culture-media');
  getCultureGrowthHistory = (id: string) => this.c.get(`/laboratory/cultures/${id}/growth`);
  addGrowthObservation = (id: string, d: any) => this.c.post(`/laboratory/cultures/${id}/growth`, d);
  getColonyCounts = (id: string) => this.c.get(`/laboratory/cultures/${id}/colony-counts`);
  addColonyCount = (id: string, d: any) => this.c.post(`/laboratory/cultures/${id}/colony-counts`, d);
  getMICResults = (id: string) => this.c.get(`/laboratory/cultures/${id}/mic-results`);
  addMICResult = (id: string, d: any) => this.c.post(`/laboratory/cultures/${id}/mic-results`, d);
  getOrganismResistance = (id: string) => this.c.get(`/laboratory/organisms/${id}/resistance`);

  // ── Reports Extended ─────────────────────────────────────────────────
  getReportTemplateSections = (id: string) => this.c.get(`/reports/template-sections/${id}`);
  getReportTemplateVariables = (id: string) => this.c.get(`/reports/template-variables/${id}`);
  getReportPrintHistory = (p?: any) => this.c.get('/reports/print-history', { params: p });
  logReportPrint = (d: any) => this.c.post('/reports/print-log', d);
  getReportDistribution = (type?: string) => this.c.get('/reports/distribution', { params: type ? { reportType: type } : {} });
  distributeReport = (d: any) => this.c.post('/reports/distribution', d);

  // ── Appointment Extended ──────────────────────────────────────────────
  cancelAppointmentWithReason = (id: string, d: any) => this.c.post(`/appointments/${id}/cancel-with-reason`, d);
  getAppointmentCancellations = (dateFrom?: string) => this.c.get('/appointments/cancellations', { params: dateFrom ? { dateFrom } : {} });
  kioskCheckin = (id: string, d: any) => this.c.post(`/appointments/${id}/kiosk-checkin`, d);
  getFollowupRules = (doctorId?: string) => this.c.get('/appointments/followup-rules', { params: doctorId ? { doctorId } : {} });
  setFollowupRule = (d: any) => this.c.post('/appointments/followup-rules', d);

  // ── OT Extended ───────────────────────────────────────────────────────
  getOTPreparation = (sid: string) => this.c.get(`/ot/preparation/${sid}`);
  updateOTPreparation = (d: any) => this.c.post('/ot/preparation', d);
  getOTConsumables = (sid: string) => this.c.get(`/ot/consumables/${sid}`);
  addOTConsumable = (d: any) => this.c.post('/ot/consumables', d);
  getOTEquipmentUsage = (sid: string) => this.c.get(`/ot/equipment-usage/${sid}`);
  logOTEquipmentUsage = (d: any) => this.c.post('/ot/equipment-usage', d);


  // ── Document Management Extended ──────────────────────────────────────
  getDocumentTypes = (module?: string) => this.c.get('/dms/types', { params: module ? { module } : {} });
  createDocumentType = (d: any) => this.c.post('/dms/types', d);
  getDocumentTemplates = (typeId?: string) => this.c.get('/dms/templates', { params: typeId ? { typeId } : {} });
  createDocumentTemplate = (d: any) => this.c.post('/dms/templates', d);
  getDMSRetentionPolicies = () => this.c.get('/dms/retention');
  setDMSRetentionPolicy = (typeId: string, d: any) => this.c.put(`/dms/retention/${typeId}`, d);
  getDocumentPermissions = (docId: string) => this.c.get(`/dms/permissions/${docId}`);
  setDocumentPermission = (docId: string, role: string, d: any) => this.c.put(`/dms/permissions/${docId}/${role}`, d);
  getExpiringDocuments = (days?: number) => this.c.get('/dms/expiring', { params: days ? { days } : {} });
  getDocumentArchives = () => this.c.get('/dms/archives');

  // ── Buildings / Rooms ─────────────────────────────────────────────────
  getBuildings = (branchId?: string) => this.c.get('/enterprise/buildings', { params: branchId ? { branchId } : {} });
  createBuilding = (d: any) => this.c.post('/enterprise/buildings', d);
  getBuildingFloors = (id: string) => this.c.get(`/enterprise/buildings/${id}/floors`);
  addFloor = (id: string, d: any) => this.c.post(`/enterprise/buildings/${id}/floors`, d);
  getRooms = (floorId?: string) => this.c.get('/enterprise/rooms', { params: floorId ? { floorId } : {} });
  getRoomCategories = () => this.c.get('/enterprise/room-categories');
  createRoom = (d: any) => this.c.post('/enterprise/rooms', d);
  getRoomBeds = (roomId: string) => this.c.get(`/enterprise/rooms/${roomId}/beds`);
  addRoomBed = (roomId: string, d: any) => this.c.post(`/enterprise/rooms/${roomId}/beds`, d);
  getBedCategories = () => this.c.get('/enterprise/bed-categories');
  getBedStatusHistory = (bedId: string) => this.c.get(`/enterprise/beds/${bedId}/status-history`);
  getBedOccupancyHistory = (bedId: string) => this.c.get(`/enterprise/beds/${bedId}/occupancy`);

  // ── Dashboard ─────────────────────────────────────────────────────────
  getDashboardWidgets = () => this.c.get('/enterprise/dashboard/widgets');
  createDashboardWidget = (d: any) => this.c.post('/enterprise/dashboard/widgets', d);
  getDashboardLayouts = (role?: string) => this.c.get('/enterprise/dashboard/layouts', { params: role ? { role } : {} });
  saveDashboardLayout = (d: any) => this.c.post('/enterprise/dashboard/layouts', d);
  getDashboardFilters = (layoutId: string) => this.c.get(`/enterprise/dashboard/filters/${layoutId}`);

  // ── Doctor/Department Branch ──────────────────────────────────────────
  getDoctorBranches = (doctorId?: string) => this.c.get('/enterprise/doctor-branches', { params: doctorId ? { doctorId } : {} });
  assignDoctorBranch = (d: any) => this.c.post('/enterprise/doctor-branches', d);
  getDepartmentBranches = (branchId?: string) => this.c.get('/enterprise/department-branches', { params: branchId ? { branchId } : {} });

  // ── Service Bundles & Pricing ─────────────────────────────────────────
  getServiceBundles = () => this.c.get('/enterprise/service-bundles');
  createServiceBundle = (d: any) => this.c.post('/enterprise/service-bundles', d);
  getPricingRules = () => this.c.get('/enterprise/pricing-rules');
  createPricingRule = (d: any) => this.c.post('/enterprise/pricing-rules', d);

  // ── Laundry ───────────────────────────────────────────────────────────
  getLaundryItems = () => this.c.get('/enterprise/laundry/items');
  getLaundryDispatches = () => this.c.get('/enterprise/laundry/dispatches');
  dispatchLaundry = (d: any) => this.c.post('/enterprise/laundry/dispatch', d);
  returnLaundry = (id: string, d: any) => this.c.post(`/enterprise/laundry/${id}/return`, d);
  reportLaundryDamage = (id: string, d: any) => this.c.post(`/enterprise/laundry/${id}/damage`, d);

  // ── Research ──────────────────────────────────────────────────────────
  getClinicalTrials = (status?: string) => this.c.get('/enterprise/research/trials', { params: status ? { status } : {} });
  createClinicalTrial = (d: any) => this.c.post('/enterprise/research/trials', d);
  getResearchPatients = (trialId: string) => this.c.get(`/enterprise/research/trials/${trialId}/patients`);
  enrollResearchPatient = (trialId: string, d: any) => this.c.post(`/enterprise/research/trials/${trialId}/enroll`, d);
  getResearchConsent = (trialId: string, pid?: string) => this.c.get(`/enterprise/research/trials/${trialId}/consent`, { params: pid ? { patientId: pid } : {} });
  recordResearchConsent = (d: any) => this.c.post('/enterprise/research/consent', d);
  getResearchDocuments = (trialId: string) => this.c.get(`/enterprise/research/trials/${trialId}/documents`);

  // ── Fluid Balance ─────────────────────────────────────────────────────
  getFluidBalance = (pid: string, aid?: string) => this.c.get(`/enterprise/fluid-balance/${pid}`, { params: aid ? { admissionId: aid } : {} });
  addFluidBalance = (d: any) => this.c.post('/enterprise/fluid-balance', d);

  // ── CSSD Instruments & Cycles ─────────────────────────────────────────
  getCSSDInstruments = () => this.c.get('/enterprise/cssd/instruments');
  createCSSDInstrument = (d: any) => this.c.post('/enterprise/cssd/instruments', d);
  getCSSDAutoclaves = () => this.c.get('/enterprise/cssd/autoclaves');
  getCSSDCycles = (autoclaveId?: string) => this.c.get('/enterprise/cssd/cycles', { params: autoclaveId ? { autoclaveId } : {} });
  recordCSSDCycle = (d: any) => this.c.post('/enterprise/cssd/cycles', d);

  // ── Radiology Protocols & Contrast ────────────────────────────────────
  getRadiologyProtocols = (modalityId?: string) => this.c.get('/enterprise/radiology/protocols', { params: modalityId ? { modalityId } : {} });
  getContrastMaster = () => this.c.get('/enterprise/radiology/contrast');

  // ── Lab Interfaces & Reagent Batches ─────────────────────────────────
  getAnalyzerInterfaces = (id: string) => this.c.get(`/enterprise/lab/interfaces/${id}`);
  addAnalyzerInterface = (d: any) => this.c.post('/enterprise/lab/interfaces', d);
  getReagentBatches = (id: string) => this.c.get(`/enterprise/lab/batches/${id}`);
  addReagentBatch = (d: any) => this.c.post('/enterprise/lab/batches', d);

  // ── HR Shift History ──────────────────────────────────────────────────
  getEmployeeShiftHistory = (id: string) => this.c.get(`/enterprise/hr/shift-history/${id}`);
  recordEmployeeShift = (d: any) => this.c.post('/enterprise/hr/shift-history', d);

  // ── Infection Antibiogram ─────────────────────────────────────────────
  getInfectionAntibiogram = (year?: number, quarter?: number) => this.c.get('/enterprise/infection/antibiogram', { params: { year, quarter } });
  recordAntibiogramData = (d: any) => this.c.post('/enterprise/infection/antibiogram', d);

  // ── Settings Extended ─────────────────────────────────────────────────
  getThemes = () => this.c.get('/settings/themes');
  getLicenseMaster = () => this.c.get('/settings/license-master');
  getStoreBranchMapping = (branchId?: string) => this.c.get('/settings/store-branch', { params: branchId ? { branchId } : {} });
  mapStoreBranch = (d: any) => this.c.post('/settings/store-branch', d);
  getUserBranchMappings = (userId?: string) => this.c.get('/settings/user-branch', { params: userId ? { userId } : {} });
  mapUserBranch = (d: any) => this.c.post('/settings/user-branch', d);
  getBranchSettings = (branchId: string) => this.c.get(`/settings/branch-settings/${branchId}`);
  setBranchSetting = (branchId: string, key: string, value: string) => this.c.put(`/settings/branch-settings/${branchId}/${key}`, { value });
  getBranchDepartments = (branchId: string) => this.c.get(`/settings/branch-departments/${branchId}`);
  addBranchDepartment = (d: any) => this.c.post('/settings/branch-departments', d);
  getBranchBankAccounts = (branchId: string) => this.c.get(`/settings/branch-bank-accounts/${branchId}`);
  addBranchBankAccount = (d: any) => this.c.post('/settings/branch-bank-accounts', d);
  getModuleSettings = (module: string, branchId?: string) => this.c.get(`/enterprise/module-settings/${module}`, { params: branchId ? { branchId } : {} });
  setModuleSetting = (module: string, key: string, value: string, branchId?: string) => this.c.put(`/enterprise/module-settings/${module}/${key}`, { value }, { params: branchId ? { branchId } : {} });
  getPriceBranch = (branchId: string, itemType?: string) => this.c.get(`/enterprise/price-branch/${branchId}`, { params: itemType ? { itemType } : {} });
  setPriceBranch = (d: any) => this.c.post('/enterprise/price-branch', d);
  getReceiptSeries = (branchId?: string) => this.c.get('/enterprise/receipt-series', { params: branchId ? { branchId } : {} });
  getNextReceiptNumber = (branchId: string) => this.c.get(`/enterprise/receipt-series/next/${branchId}`);
  recordGatewayTransaction = (d: any) => this.c.post('/enterprise/gateway-transactions', d);

  // ── Terminology ───────────────────────────────────────────────────────
  searchICD10Local = (q: string) => this.c.get('/terminology/icd10/search', { params: { q } });
  searchLOINCLocal = (q: string) => this.c.get('/terminology/loinc/search', { params: { q } });
  searchSNOMEDLocal = (q: string) => this.c.get('/terminology/snomed/search', { params: { q } });
  getMasterLanguages = () => this.c.get('/terminology/languages');
  getMasterCurrencies = () => this.c.get('/terminology/currencies');
  getMasterCountries = () => this.c.get('/terminology/countries');
  getMasterStates = (countryId?: string) => this.c.get('/terminology/states', { params: countryId ? { countryId } : {} });
  getMasterCities = (stateId?: string) => this.c.get('/terminology/cities', { params: stateId ? { stateId } : {} });

  // ── Nursing Care Plans ────────────────────────────────────────────────
  getNursingCarePlans = (pid: string) => this.c.get(`/enterprise/nursing/care-plans/${pid}`);


  // ── Enterprise Final Batch ────────────────────────────────────────────
  getBuildingBlocks = (buildingId: string) => this.c.get(`/enterprise/buildings/${buildingId}/blocks`);
  addBuildingBlock = (buildingId: string, d: any) => this.c.post(`/enterprise/buildings/${buildingId}/blocks`, d);
  getBedFeatures = (bedId: string) => this.c.get(`/enterprise/beds/${bedId}/features`);
  getASTPanels = () => this.c.get('/enterprise/ast-panels');
  createASTPannel = (d: any) => this.c.post('/enterprise/ast-panels', d);
  getAuditRestoreLog = () => this.c.get('/enterprise/audit-restore-log');
  getBankReconciliation = (year?: number) => this.c.get('/enterprise/bank-reconciliation', { params: year ? { year } : {} });
  createBankReconciliation = (d: any) => this.c.post('/enterprise/bank-reconciliation', d);
  getFinancialClosingList = () => this.c.get('/enterprise/financial-closing');
  createFinancialClosing = (d: any) => this.c.post('/enterprise/financial-closing', d);
  getFinancialYears = () => this.c.get('/enterprise/financial-years');
  getGSTReturns = (financialYear?: string) => this.c.get('/enterprise/gst-returns', { params: financialYear ? { financialYear } : {} });
  getClaimResubmissions = (id: string) => this.c.get(`/enterprise/claims/${id}/resubmissions`);
  addClaimResubmission = (id: string, d: any) => this.c.post(`/enterprise/claims/${id}/resubmit`, d);
  getCriticalValueRanges = (testId?: string) => this.c.get('/enterprise/critical-value-ranges', { params: testId ? { testId } : {} });
  setCriticalValueRange = (d: any) => this.c.post('/enterprise/critical-value-ranges', d);
  getCultureOrganismAntibiotic = (id: string) => this.c.get(`/enterprise/culture-organism/${id}`);
  getDicomStudies = (orderId: string) => this.c.get(`/enterprise/dicom/studies/${orderId}`);
  getDicomSeries = (studyId: string) => this.c.get(`/enterprise/dicom/series/${studyId}`);
  getDicomInstances = (seriesId: string) => this.c.get(`/enterprise/dicom/instances/${seriesId}`);
  getDietDeliveryRecords = (patientId?: string) => this.c.get('/enterprise/diet-delivery', { params: patientId ? { patientId } : {} });
  getDoctorProfiles = (doctorId?: string) => this.c.get('/enterprise/doctor-profiles', { params: doctorId ? { doctorId } : {} });
  getDoctorIncentives = (doctorId?: string) => this.c.get('/enterprise/doctor-incentives', { params: doctorId ? { doctorId } : {} });
  getDrugSerialNumbers = (id: string) => this.c.get(`/enterprise/drugs/${id}/serial-numbers`);
  getEmployeeDocuments = (id: string) => this.c.get(`/enterprise/employee-documents/${id}`);
  getEquipmentCalibration = (eid?: string) => this.c.get('/enterprise/equipment-calibration', { params: eid ? { equipmentId: eid } : {} });
  getEquipmentSpareInventory = (partId: string) => this.c.get(`/enterprise/equipment-spare/${partId}`);
  getInfectionAudit = () => this.c.get('/enterprise/infection/audit');
  createInfectionAudit = (d: any) => this.c.post('/enterprise/infection/audit', d);
  getMealSchedule = (patientId?: string) => this.c.get('/enterprise/meal-schedule', { params: patientId ? { patientId } : {} });
  getOrgLocalization = () => this.c.get('/enterprise/org/localization');
  updateOrgLocalization = (d: any) => this.c.put('/enterprise/org/localization', d);
  getPatientReportsAccess = (pid: string) => this.c.get(`/enterprise/patient-reports-access/${pid}`);
  getRadiologyAnnotations = (instanceId: string) => this.c.get(`/enterprise/radiology/annotations/${instanceId}`);
  addRadiologyAnnotation = (d: any) => this.c.post('/enterprise/radiology/annotations', d);
  getRareDonorRegistry = (bloodGroup?: string) => this.c.get('/enterprise/rare-donor-registry', { params: bloodGroup ? { bloodGroup } : {} });
  getResearchProtocols = (trialId: string) => this.c.get(`/enterprise/research/trials/${trialId}/protocols`);
  addResearchProtocol = (d: any) => this.c.post('/enterprise/research/protocols', d);
  getVillages = (districtId?: string) => this.c.get('/enterprise/geo/villages', { params: districtId ? { districtId } : {} });
  getBranchWorkstations = (branchId: string) => this.c.get(`/enterprise/branch-workstations/${branchId}`);
  getVoucherTypes = () => this.c.get('/enterprise/voucher-types');
  getAccountGroups = () => this.c.get('/enterprise/account-groups');
  getRevenueMetrics = (year?: number, month?: number) => this.c.get('/enterprise/revenue-metrics', { params: { year, month } });
  getPurchaseReturnItems = (returnId?: string) => this.c.get('/enterprise/purchase-return-items', { params: returnId ? { returnId } : {} });
  getNotificationEvents = () => this.c.get('/enterprise/notification-events');
  getAppointmentPrints = () => this.c.get('/enterprise/appointment-prints');
  getVisitPolicyChecks = (visitId?: string) => this.c.get('/enterprise/visit-policy-check', { params: visitId ? { visitId } : {} });
  getMicBreakpointMaster = (organism?: string) => this.c.get('/enterprise/mic-breakpoints', { params: organism ? { organism } : {} });
  getBranchLocations = (branchId: string) => this.c.get(`/settings/branch-locations/${branchId}`);
  addBranchLocation = (d: any) => this.c.post('/settings/branch-locations', d);
  getOrgSettingsAll = () => this.c.get('/settings/org-settings');
  setOrgSetting = (key: string, value: string) => this.c.put(`/settings/org-settings/${key}`, { value });
  getOrgDomains = () => this.c.get('/settings/org-domains');
  addOrgDomain = (d: any) => this.c.post('/settings/org-domains', d);
  getPrinterBranchMapping = (branchId?: string) => this.c.get('/settings/printer-branch', { params: branchId ? { branchId } : {} });


  // ── Auth Infrastructure ───────────────────────────────────────────────
  getAPIKeys = () => this.c.get('/rbac/api-keys');
  createAPIKey = (d: any) => this.c.post('/rbac/api-keys', d);
  revokeAPIKey = (id: string) => this.c.put(`/auth/api-keys/${id}/revoke`, {});
  getMFAConfig = () => this.c.get('/rbac/mfa-config');
  enableMFA = (d: any) => this.c.post('/rbac/mfa-enable', d);
  getMFADevices = () => this.c.get('/rbac/mfa-devices');
  getOAuthClients = () => this.c.get('/rbac/oauth-clients');
  createOAuthClient = (d: any) => this.c.post('/rbac/oauth-clients', d);
  getDeviceRegistry = () => this.c.get('/rbac/devices');
  registerDevice = (d: any) => this.c.post('/auth/devices/register', d);
  getRefreshTokens = () => this.c.get('/rbac/refresh-tokens');
  revokeRefreshToken = (id: string) => this.c.put(`/auth/refresh-tokens/${id}/revoke`, {});
  getTrustedDevices = () => this.c.get('/rbac/trusted-devices');
  // ── Scheduler / Infra ─────────────────────────────────────────────────
  getCronHistory = (limit?: number) => this.c.get('/enterprise/cron-history', { params: limit ? { limit } : {} });
  getPushQueue = (userId?: string) => this.c.get('/enterprise/push-queue', { params: userId ? { userId } : {} });
  getPayrollDetails = (employeeId?: string, month?: string) => this.c.get('/enterprise/payroll-details', { params: { employeeId, month } });


  getOCRResults = (docId: string) => this.c.get(`/dms/ocr/${docId}`);
  saveOCRResult = (docId: string, d: any) => this.c.post(`/dms/ocr/${docId}`, d);


  // ── HRMS Candidates & Timezones ──────────────────────────────────────
  getCandidates = (recruitmentId?: string) => this.c.get('/hrms/candidates', { params: recruitmentId ? { recruitmentId } : {} });
  createCandidate = (d: any) => this.c.post('/hrms/candidates', d);
  updateCandidateStatus = (id: string, status: string) => this.c.put(`/hrms/candidates/${id}/status`, { status });
  getTimezones = () => this.c.get('/hrms/timezones');
  addTimezone = (d: any) => this.c.post('/hrms/timezones', d);



  // ══════════════════════════════════════════════════════════════════════
  // COMPLETE COVERAGE: All remaining backend endpoints
  // ══════════════════════════════════════════════════════════════════════

  // ── ABDM / FHIR Fidelius ────────────────────────────────────────────
  fideliusGenerateKeys = () => this.c.post('/abdm/fidelius/generate-keys', {});
  fideliusEncrypt = (d: any) => this.c.post('/abdm/fidelius/encrypt', d);
  fideliusDecrypt = (d: any) => this.c.post('/abdm/fidelius/decrypt', d);
  getABDMCareContexts = (pid: string) => this.c.get(`/abdm/care-contexts/${pid}`);
  getABHAProfile = (abhaId: string) => this.c.get(`/abha/${abhaId}`);
  createABHA = (d: any) => this.c.post('/abdm/abha/create', d);
  generateABHAOTP = (d: any) => this.c.post('/abdm/abha/generate-otp', d);
  verifyABHAOTP = (d: any) => this.c.post('/abdm/abha/verify-otp', d);
  searchABHA = (q: string) => this.c.get('/abdm/abha/search', { params: { q } });

  // ── FHIR R4 Resources ───────────────────────────────────────────────
  getFHIRMetadata = () => this.c.get('/fhir/r4/metadata');
  getFHIRPatient = (id: string) => this.c.get(`/fhir/r4/Patient/${id}`);
  searchFHIRPatients = (p?: any) => this.c.get('/fhir/r4/Patient', { params: p });
  getFHIREverything = (pid: string) => this.c.get(`/fhir/r4/Patient/${pid}/$everything`);
  getFHIRObservation = (id: string) => this.c.get(`/fhir/r4/Observation/${id}`);
  getFHIRCondition = (id: string) => this.c.get(`/fhir/r4/Condition/${id}`);
  getFHIRMedicationRequest = (id: string) => this.c.get(`/fhir/r4/MedicationRequest/${id}`);
  getFHIRDiagnosticReport = (id: string) => this.c.get(`/fhir/r4/DiagnosticReport/${id}`);
  getFHIRAllergyIntolerance = (id: string) => this.c.get(`/fhir/r4/AllergyIntolerance/${id}`);
  getFHIRImmunization = (id: string) => this.c.get(`/fhir/r4/Immunization/${id}`);
  postFHIRBundle = (d: any) => this.c.post('/fhir/r4/Bundle', d);

  // ── HL7 Messaging ───────────────────────────────────────────────────
  receiveHL7 = (d: any) => this.c.post('/hl7/receive', d);
  sendHL7ADTAdmit = (pid: string, aid: string) => this.c.post(`/hl7/adt/admit/${pid}/${aid}`, {});
  sendHL7ORMLabOrder = (pid: string, rid: string) => this.c.post(`/hl7/orm/lab/${pid}/${rid}`, {});
  parseHL7Message = (d: any) => this.c.post('/hl7/parse', d);

  // ── Approval Workflow ────────────────────────────────────────────────
  submitApproval = (d: any) => this.c.post('/approval/submit', d);
  getApprovalStatus = (id: string) => this.c.get(`/approval/status/${id}`);

  // ── Blood Bank Extended ──────────────────────────────────────────────
  getBloodReturns = (p?: any) => this.c.get('/bloodbank/returns', { params: p });
  getBloodDisposals = (p?: any) => this.c.get('/bloodbank/disposals', { params: p });
  createBloodDisposal = (d: any) => this.c.post('/bloodbank/disposals', d);

  // ── CPOE Extended ───────────────────────────────────────────────────
  getCPOECancellations = (p?: any) => this.c.get('/cpoe/cancellations', { params: p });

  // ── eMAR Extended ───────────────────────────────────────────────────
  getPRNMedications = (admissionId: string) => this.c.get(`/emar/reports/prn/${admissionId}`);

  // ── Finance Extended ────────────────────────────────────────────────
  getCashTransactions = (p?: any) => this.c.get('/finance/cash/transactions', { params: p });
  createCashTransaction = (d: any) => this.c.post('/finance/cash/transactions', d);

  // ── HRMS Stats ──────────────────────────────────────────────────────
  getHRMSStats = () => this.c.get('/hrms/stats');

  // ── Insurance Extended ──────────────────────────────────────────────
  appealClaimRejection = (id: string, d: any) => this.c.post(`/insurance/rejections/${id}/appeal`, d);

  // ── Lab Extended ────────────────────────────────────────────────────
  getAntibioticMaster = (q?: string) => this.c.get('/laboratory/antibiotic-master', { params: q ? { q } : {} });
  addAntibioticMaster = (d: any) => this.c.post('/laboratory/antibiotic-master', d);
  getOrganismMasterList = (q?: string) => this.c.get('/laboratory/organism-master', { params: q ? { q } : {} });
  addOrganismMaster = (d: any) => this.c.post('/laboratory/organism-master', d);

  // ── NABH Extended ───────────────────────────────────────────────────
  importNABHCompliance = (d: any) => this.c.post('/quality/compliance/import', d);
  getIndicatorHistory = (id: string) => this.c.get(`/quality/indicators/${id}/history`);
  calculateNABHIndicators = (period: string) => this.c.post(`/quality/indicators/calculate/${period}`, {});

  // ── Radiology Extended ──────────────────────────────────────────────
  getRadiologyImages = (studyId: string) => this.c.get(`/radiology/images/${studyId}`);

  // ── RBAC Extended ───────────────────────────────────────────────────
  loginV2 = (d: any) => this.c.post('/rbac/login-v2', d);

  // ── Reports Extended ────────────────────────────────────────────────
  getDoctorPerformanceReport = (p?: any) => this.c.get('/reports/doctor-performance', { params: p });
  getAppointmentSummaryReport = (p?: any) => this.c.get('/reports/appointment-summary', { params: p });
  getNoShowReport = (p?: any) => this.c.get('/reports/no-show', { params: p });
  getAppointmentCancellationReport = (p?: any) => this.c.get('/reports/cancellations', { params: p });

  // ── Scheduling Extended ─────────────────────────────────────────────
  getDoctorAvailability = (doctorId: string, p?: any) => this.c.get(`/scheduling/availability/${doctorId}`, { params: p });

  // ── Terminology Extended ────────────────────────────────────────────
  getICD10Chapters = () => this.c.get('/terminology/icd10/chapters');
  getICD10Chapter = (chapter: string) => this.c.get(`/terminology/icd10/chapter/${chapter}`);
  getICD10Code = (code: string) => this.c.get(`/terminology/icd10/${code}`);
  importICD10 = (d: any) => this.c.post('/terminology/icd10/import', d);
  getSNOMEDHierarchies = () => this.c.get('/terminology/snomed/hierarchies');
  getCommonSNOMEDConcepts = () => this.c.get('/terminology/snomed/common');
  getSNOMEDConcept = (id: string) => this.c.get(`/terminology/snomed/${id}`);
  importSNOMED = (d: any) => this.c.post('/terminology/snomed/import', d);
  searchLOINCCodes = (q: string) => this.c.get('/terminology/loinc/search', { params: { q } });

  // ── Settings Extended ───────────────────────────────────────────────
  getAppSettingsBulk = (keys: string[]) => this.c.post('/settings/app-settings/bulk', { keys });
  getAppointmentsByDepartment = (p?: any) => this.c.get('/settings/appointments-by-department', { params: p });
  getAppointmentsByDoctor = (p?: any) => this.c.get('/settings/appointments-by-doctor', { params: p });
  sendAppointmentReminder = (id: string) => this.c.post(`/settings/appointment-reminder/${id}`, {});

  // ── Patient Extended ────────────────────────────────────────────────
  autoRegisterPatient = (d: any) => this.c.post('/patients/auto-register', d);
  getPatientFeedback = (pid: string) => this.c.get(`/patients/${pid}/feedback`);
  addPatientFeedback = (pid: string, d: any) => this.c.post(`/patients/${pid}/feedback`, d);
  getPatientFamilyHistory = (pid: string) => this.c.get(`/patients/${pid}/family-history`);
  addPatientFamilyHistory = (pid: string, d: any) => this.c.post(`/patients/${pid}/family-history`, d);
  getPatientMedicalHistory = (pid: string) => this.c.get(`/patients/${pid}/medical-history`);
  addPatientMedicalHistory = (pid: string, d: any) => this.c.post(`/patients/${pid}/medical-history`, d);
  getPatientSurgicalHistory = (pid: string) => this.c.get(`/patients/${pid}/surgical-history`);
  addPatientSurgicalHistory = (pid: string, d: any) => this.c.post(`/patients/${pid}/surgical-history`, d);
  getPatientAllergies = (pid: string) => this.c.get(`/patients/${pid}/allergies`);
  addPatientAllergy = (pid: string, d: any) => this.c.post(`/patients/${pid}/allergies`, d);
  getPatientProblems = (pid: string) => this.c.get(`/patients/${pid}/problems`);
  addPatientProblem = (pid: string, d: any) => this.c.post(`/patients/${pid}/problems`, d);
  getPatientDocuments = (pid: string) => this.c.get(`/patients/${pid}/documents`);
  getPatientVisits = (pid: string) => this.c.get(`/patients/${pid}/visits`);
  getPatients = (p?: any) => this.c.get('/patients', { params: p });
  getTodayPatients = () => this.c.get('/patients/today-count');
  getVitals = (pid: string) => this.c.get(`/patients/${pid}/vitals`);
  getOPDVitals = (pid: string) => this.c.get(`/opd/vitals/${pid}`);

  // ── IPD Extended ────────────────────────────────────────────────────
  getAdmissions = (p?: any) => this.c.get('/ipd/admissions', { params: p });
  getAdmissionById = (id: string) => this.c.get(`/ipd/admissions/${id}`);
  getAdmissionBill = (id: string) => this.c.get(`/ipd/admissions/${id}/bill`);
  getWards = () => this.c.get('/enterprise/wards');
  getBeds = (wardId?: string) => this.c.get('/ipd/beds', { params: wardId ? { wardId } : {} });
  transferPatient = (id: string, d: any) => this.c.post(`/ipd/admissions/${id}/transfer`, d);
  dischargePatient = (id: string, d: any) => this.c.post(`/ipd/admissions/${id}/discharge`, d);
  addRoundNote = (id: string, d: any) => this.c.post(`/ipd/admissions/${id}/round`, d);
  createCpoeOrder = (d: any) => this.c.post('/cpoe/orders', d);

  // ── ICU Extended ────────────────────────────────────────────────────
  getIcuAdmissions = () => this.c.get('/icu/admissions');
  createIcuAdmission = (d: any) => this.c.post('/icu/admissions', d);
  getIcuMonitoring = (id: string) => this.c.get(`/icu/admissions/${id}/monitoring`);
  addIcuMonitoring = (id: string, d: any) => this.c.post(`/icu/admissions/${id}/monitoring`, d);

  // ── Emergency Extended ──────────────────────────────────────────────
  getEmergencyCases = (p?: any) => this.c.get('/emergency/cases', { params: p });
  registerEmergencyCase = (d: any) => this.c.post('/emergency/cases', d);
  getEmergencyStats = () => this.c.get('/emergency/stats');
  disposeEmergencyCase = (id: string, d: any) => this.c.put(`/emergency/cases/${id}/dispose`, d);

  // ── Analytics Extended ──────────────────────────────────────────────
  getAnalyticsSummary = () => this.c.get('/analytics/dashboard');
  getRevenueAnalytics = (p?: any) => this.c.get('/analytics/financial', { params: p });
  getTopDiagnoses = (p?: any) => this.c.get('/analytics/clinical', { params: p });
  getBedOccupancy = () => this.c.get('/analytics/operational');

  // ── Telemedicine Extended ────────────────────────────────────────────
  getTelemedicineSessions = (p?: any) => this.c.get('/telemedicine/sessions', { params: p });
  getTeleconsultations = (p?: any) => this.c.get('/telemedicine/consultations', { params: p });
  createTelemedicineSession = (d: any) => this.c.post('/telemedicine/sessions', d);
  startTelemedicineSession = (id: string) => this.c.post(`/telemedicine/sessions/${id}/start`, {});

  // ── Doctor Scheduling ────────────────────────────────────────────────
  getDoctors = (p?: any) => this.c.get('/users/doctors', { params: p });
  getDoctorSchedule = (doctorId: string, p?: any) => this.c.get(`/scheduling/doctors/${doctorId}/schedule`, { params: p });
  createDoctorLeave = (d: any) => this.c.post('/scheduling/leaves', d);

  // ── Blood Bank Extended ──────────────────────────────────────────────
  registerDonor = (d: any) => this.c.post('/bloodbank/donors', d);
  getBloodDonors = (p?: any) => this.c.get('/bloodbank/donors', { params: p });

  // ── NABH Extended ────────────────────────────────────────────────────
  getQualityChecklists = (p?: any) => this.c.get('/quality/checklists', { params: p });
  updateQualityChecklist = (id: string, d: any) => this.c.put(`/quality/checklists/${id}`, d);
  getQualityIncidents = (p?: any) => this.c.get('/quality/incidents', { params: p });
  createQualityIncident = (d: any) => this.c.post('/quality/incidents', d);
  getQualityAudit = (p?: any) => this.c.get('/quality/quality-audits', { params: p });
  getKPIHistory = (p?: any) => this.c.get('/quality/kpi', { params: p });
  recordKPIData = (d: any) => this.c.post('/quality/kpi', d);

  // ── Audit Extended ───────────────────────────────────────────────────
  getAuditLogs          = (p?: any) => this.c.get('/audit/logs', { params: p });
  getUserSessions = () => this.c.get('/audit/access-logs');
  getAuditAlerts        = (p?: any) => this.c.get('/audit/alerts', { params: p });
  exportAuditLogs = (p?: any) => this.c.post('/audit/export-log', p);
  getChangeLog = (p?: any) => this.c.get('/audit-logs/', { params: p });

  // ── Settings Extended ────────────────────────────────────────────────
  getBranches = () => this.c.get('/settings/branches');
  createBranch = (d: any) => this.c.post('/settings/branches', d);
  getWorkstations = (branchId?: string) => this.c.get('/settings/workstations', { params: branchId ? { branchId } : {} });
  createWorkstation = (d: any) => this.c.post('/settings/workstations', d);
  seedDefaultBranch     = (d?: any) => this.c.post('/settings/workstations/public', d || {});
  getUserAssignments = (p?: any) => this.c.get('/settings/user-station-assignments', { params: p });
  assignUserToStation = (d: any) => this.c.post('/settings/user-station-assignments', d);
  getRbacUsers          = (p?: any) => this.c.get('/rbac/users', { params: p });

  // ── Labs Helper Methods ──────────────────────────────────────────────
  getLabAntibiotics = () => this.c.get('/laboratory/antibiotic-master');
  getLabOrganisms = () => this.c.get('/laboratory/organism-master');
  getLabCultures = () => this.c.get('/laboratory/culture-templates');
  saveLabCulture = (d: any) => this.c.post('/laboratory/culture-templates', d);

  // ── Report Templates Extended ────────────────────────────────────────
  createReportTemplate = (d: any) => this.c.post('/reports/report-templates', d);
  getInterpretationRules = () => this.c.get('/reports/interpretation-rules');
  createInterpretationRule = (d: any) => this.c.post('/reports/interpretation-rules', d);

  // ── Biomedical Extended ──────────────────────────────────────────────
  getBreakdowns         = (p?: any) => this.c.get('/biomedical/breakdowns', { params: p });
  getCalibrations       = (p?: any) => this.c.get('/biomedical/calibrations', { params: p });
  createEquipment = (d: any) => this.c.post('/biomedical/equipment', d);
  createAmc = (d: any) => this.c.post('/biomedical/amc', d);
  completePM = (id: string, d: any) => this.c.put(`/biomedical/pm/${id}/complete`, d);

  // ── FIXES: Missing methods & updated signatures ─────────────────────
  getPatientById        = (id: string) => this.c.get(`/patients/${id}`);
  getDoctorSlots        = (doctorId: string, date: string) => this.c.get(`/scheduling/doctors/${doctorId}/slots`, { params: { date } });
  createAdmission       = (d: any) => this.c.post('/ipd/admissions', d);

  // Emergency - missing methods
  updateEmergencyCase = (id: string, d: any) => this.c.put(`/emergency/cases/${id}`, d);

  // Surgical checklist
  createSurgicalChecklistV2 = (d: any) => this.c.post('/surgical-checklist', d);

  // Credit notes

  // HR aliases
  getHREmployees = (p?: any) => this.c.get('/hrms/employees', { params: p });
  getHRLeaves = (p?: any) => this.c.get('/hrms/leaves', { params: p });

  // Scheduling

  // Diet
  getDietMaster = () => this.c.get('/diet/master');
  getDietAssessments = (p?: any) => this.c.get('/diet/assessments', { params: p });

  // GST

  // Housekeeping

  // Analytics
  getAnalyticsOperational = (p?: any) => this.c.get('/analytics/operational', { params: p });

  // Masters
  saveMaster = (type: string, d: any) => this.c.post(`/masters/${type}`, d);

  // Settings extended
  getUserStationAssignments = (p?: any) => this.c.get('/settings/user-station-assignments', { params: p });
  setUserStationAssignment = (d: any) => this.c.post('/settings/user-station-assignments', d);
  getSettingsMasters = (type: string) => this.c.get(`/settings/masters/${type}`);

  // FHIR
  getFhirSubscriptions = () => this.c.get('/fhir/subscriptions');
  createFhirSubscription = (d: any) => this.c.post('/fhir/subscriptions', d);
  getFhirResources = () => this.c.get('/fhir/resources');

  // Billing extended
  getBillingCreditNotes = (p?: any) => this.c.get('/billing/credit-notes', { params: p });
  createBillingCreditNote = (d: any) => this.c.post('/billing/credit-notes', d);

  // Finance extended

  // Reports
  getReportTemplates = (p?: any) => this.c.get('/reports/report-templates', { params: p });

  // Telemedicine
  getTelemedicineConsultations = (p?: any) => this.c.get('/telemedicine/consultations', { params: p });

  // Audit extended
  getAuditRetention = () => this.c.get('/audit/retention');

  // Consents
  recordConsent = (d: any) => this.c.post('/consents/record', d);

  // Pharmacy
  getOTCSales = (p?: any) => this.c.get('/pharmacy/otc', { params: p });

  // Advance payments
  getPatientAdvancePayments = (patientId: string) => this.c.get(`/advance-payments/${patientId}`);

  // Lab
  getLabCultureTemplates = () => this.c.get('/laboratory/culture-templates');
  getLabTestRequests = (p?: any) => this.c.get('/laboratory/test-requests', { params: p });

  // RBAC
  getApiKeys = () => this.c.get('/auth/api-keys');

  // MOH 505 — Weekly Epidemic Monitoring / DSR Register
  getMoh505Stats = (branchId?: string) => this.c.get('/moh505/stats', { params: branchId ? { branchId } : {} });
  getMoh505Reports = (p?: any) => this.c.get('/moh505', { params: p });
  getMoh505Report = (id: string) => this.c.get(`/moh505/${id}`);
  createMoh505Report = (d: any) => this.c.post('/moh505', d);
  updateMoh505Report = (id: string, d: any) => this.c.patch(`/moh505/${id}`, d);
  submitMoh505Report = (id: string) => this.c.post(`/moh505/${id}/submit`, {});
  deleteMoh505Report = (id: string) => this.c.delete(`/moh505/${id}`);
  moh505AutoTally = (weekEnding: string, branchId?: string) => this.c.get('/moh505/auto-tally', { params: { weekEnding, branchId } });

  getMohRegisterCatalog = () => this.c.get('/moh-registers/catalog');
  getMohRegisters = (formCode?: string) => this.c.get('/moh-registers', { params: formCode ? { formCode } : {} });
  getMohRegister = (id: string) => this.c.get(`/moh-registers/${id}`);
  createMohRegister = (d: any) => this.c.post('/moh-registers', d);
  updateMohRegister = (id: string, d: any) => this.c.patch(`/moh-registers/${id}`, d);
  submitMohRegister = (id: string) => this.c.post(`/moh-registers/${id}/submit`, {});
  autoFillMohRegister = (id: string) => this.c.post(`/moh-registers/${id}/auto-fill`, {});
  deleteMohRegister = (id: string) => this.c.delete(`/moh-registers/${id}`);
  addMohRegisterRow = (id: string, data: any) => this.c.post(`/moh-registers/${id}/rows`, { data });
  updateMohRegisterRow = (id: string, rowId: string, data: any) => this.c.patch(`/moh-registers/${id}/rows/${rowId}`, { data });
  deleteMohRegisterRow = (id: string, rowId: string) => this.c.delete(`/moh-registers/${id}/rows/${rowId}`);

  getCrmLeads = (p?: any) => this.c.get('/crm', { params: p });
  getCrmDuplicates = (phone: string) => this.c.get('/crm/duplicates', { params: { phone } });
  getCrmStats = () => this.c.get('/crm/stats');
  getCrmMasters = () => this.c.get('/crm/masters');
  crmLookup = (kind: string, q?: string) => this.c.get(`/crm/lookup/${kind}`, { params: q ? { q } : {} });
  getCrmFollowupsToday = () => this.c.get('/crm/followups/today');
  getCrmFollowupsOverdue = () => this.c.get('/crm/followups/overdue');
  getCrmLead = (id: string) => this.c.get(`/crm/${id}`);
  createCrmLead = (d: any) => this.c.post('/crm', d);
  updateCrmLead = (id: string, d: any) => this.c.put(`/crm/${id}`, d);
  addCrmInteraction = (id: string, d: any) => this.c.post(`/crm/${id}/interactions`, d);
  convertCrmLead = (id: string) => this.c.post(`/crm/${id}/convert`, {});

  getMortuaryDashboard = (p?: any) => this.c.get('/mortuary/dashboard', { params: p });
  getMortuaryAlerts = (facilityId?: string) => this.c.get('/mortuary/alerts', { params: facilityId ? { facilityId } : {} });
  getMortuaryLookups = () => this.c.get('/mortuary/lookups');
  getMortuarySettings = () => this.c.get('/mortuary/settings');
  saveMortuarySetting = (d: any) => this.c.post('/mortuary/settings', d);
  getMortuaries = () => this.c.get('/mortuary/mortuaries');
  saveMortuary = (d: any) => this.c.post('/mortuary/mortuaries', d);
  getMortuaryChambers = (p?: any) => this.c.get('/mortuary/chambers', { params: p });
  saveMortuaryChamber = (d: any) => this.c.post('/mortuary/chambers', d);
  saveMortuaryLookup = (d: any) => this.c.post('/mortuary/lookups', d);
  getMortuaryBodies = (p?: any) => this.c.get('/mortuary', { params: p });
  getMortuaryBody = (id: string) => this.c.get(`/mortuary/${id}`);
  getMortuaryHistory = (id: string) => this.c.get(`/mortuary/${id}/history`);
  scanMortuaryToken = (token: string) => this.c.get(`/mortuary/scan/${token}`);
  registerMortuaryBody = (d: any) => this.c.post('/mortuary', d);
  updateMortuaryBody = (id: string, d: any) => this.c.patch(`/mortuary/${id}`, d);
  admitMortuaryBody = (id: string, d: any) => this.c.post(`/mortuary/${id}/admit`, d);
  identifyMortuaryBody = (id: string, d: any) => this.c.post(`/mortuary/${id}/identify`, d);
  assignMortuaryStorage = (id: string, d: any) => this.c.post(`/mortuary/${id}/storage`, d);
  moveMortuaryBody = (id: string, d: any) => this.c.post(`/mortuary/${id}/movement`, d);
  saveMortuaryPostMortem = (id: string, d: any) => this.c.post(`/mortuary/${id}/postmortem`, d);
  addMortuaryBelonging = (id: string, d: any) => this.c.post(`/mortuary/${id}/belongings`, d);
  returnMortuaryBelonging = (belongingId: string, d: any) => this.c.post(`/mortuary/belongings/${belongingId}/return`, d);
  transferMortuaryBody = (id: string, d: any) => this.c.post(`/mortuary/${id}/transfer`, d);
  releaseMortuaryBody = (id: string, d: any) => this.c.post(`/mortuary/${id}/release`, d);
  updateMortuaryUnclaimed = (id: string, d: any) => this.c.post(`/mortuary/${id}/unclaimed`, d);
  disposeMortuaryBody = (id: string, d: any) => this.c.post(`/mortuary/${id}/dispose`, d);
  addMortuaryDocument = (id: string, d: any) => this.c.post(`/mortuary/${id}/documents`, d);
  getMortuaryUnclaimed = () => this.c.get('/mortuary/unclaimed');
  getMortuaryReport = (kind: string, p?: any) => this.c.get(`/mortuary/reports/${kind}`, { params: p });
  getMortuaryPendingIntakes = () => this.c.get('/mortuary/pending-intakes');
  intakeMortuaryFromAdmission = (admissionId: string) => this.c.post(`/mortuary/intake/admission/${admissionId}`);
  intakeMortuaryFromEmergency = (emergencyId: string) => this.c.post(`/mortuary/intake/emergency/${emergencyId}`);
  setMortuaryLegalHold = (id: string, d: any) => this.c.post(`/mortuary/${id}/hold`, d);
  updateMortuaryFlags = (id: string, d: any) => this.c.post(`/mortuary/${id}/flags`, d);
  addMortuaryNextOfKin = (id: string, d: any) => this.c.post(`/mortuary/${id}/next-of-kin`, d);
  saveMortuaryEmbalming = (id: string, d: any) => this.c.post(`/mortuary/${id}/embalm`, d);
  addMortuaryViewing = (id: string, d: any) => this.c.post(`/mortuary/${id}/viewing`, d);
  notifyMortuaryCivilRegistry = (id: string, d: any) => this.c.post(`/mortuary/${id}/civil-registry`, d);
  raiseMortuaryCharges = (id: string) => this.c.post(`/mortuary/${id}/charges`);
  getMortuaryChamberTemp = (chamberId: string) => this.c.get(`/mortuary/chambers/${chamberId}/temperature`);
  addMortuaryChamberTemp = (chamberId: string, d: any) => this.c.post(`/mortuary/chambers/${chamberId}/temperature`, d);
  getMortuaryAnalytics = () => this.c.get('/mortuary/analytics');
  getMortuaryPmTemplates = () => this.c.get('/mortuary/pm-templates');
  saveMortuaryPmTemplate = (d: any) => this.c.post('/mortuary/pm-templates', d);
  getMortuaryPmByBody = (id: string) => this.c.get(`/mortuary/${id}/postmortem/case`);
  getMortuaryPmCase = (pmId: string) => this.c.get(`/mortuary/postmortem/${pmId}`);
  createMortuaryPmCase = (id: string, d?: any) => this.c.post(`/mortuary/${id}/postmortem/case`, d || {});
  updateMortuaryPmCase = (pmId: string, d: any) => this.c.patch(`/mortuary/postmortem/${pmId}`, d);
  saveMortuaryPmFindings = (pmId: string, sections: any[]) => this.c.post(`/mortuary/postmortem/${pmId}/findings`, { sections });
  transcribeMortuaryPm = (pmId: string, d: any) => this.c.post(`/mortuary/postmortem/${pmId}/transcription`, d);
  generateMortuaryPmDraft = (pmId: string) => this.c.post(`/mortuary/postmortem/${pmId}/ai/generate`);
  confirmMortuaryAiSuggestion = (pmId: string, suggestionId: string, d?: any) => this.c.post(`/mortuary/postmortem/${pmId}/ai/suggestions/${suggestionId}/confirm`, d || {});
  signMortuaryPm = (pmId: string, d: any) => this.c.post(`/mortuary/postmortem/${pmId}/sign`, d);
  addendumMortuaryPm = (pmId: string, reason: string) => this.c.post(`/mortuary/postmortem/${pmId}/addendum`, { reason });
  addMortuarySpecimen = (id: string, d: any) => this.c.post(`/mortuary/${id}/specimens`, d);
  updateMortuarySpecimen = (specimenId: string, d: any) => this.c.patch(`/mortuary/specimens/${specimenId}`, d);
  dispatchMortuarySpecimen = (specimenId: string, d: any) => this.c.post(`/mortuary/specimens/${specimenId}/dispatch`, d);
  addMortuarySpecimenCustody = (specimenId: string, d: any) => this.c.post(`/mortuary/specimens/${specimenId}/custody`, d);
  addMortuaryEvidence = (id: string, d: any) => this.c.post(`/mortuary/${id}/evidence`, d);
  addMortuaryEvidenceCustody = (evidenceId: string, d: any) => this.c.post(`/mortuary/evidence/${evidenceId}/custody`, d);
  getMortuaryBodyBilling = (id: string) => this.c.get(`/mortuary/${id}/billing`);
  getMortuaryCompliance = (id: string) => this.c.get(`/mortuary/${id}/compliance`);
  orderMortuaryImaging = (id: string, d: any) => this.c.post(`/mortuary/${id}/imaging`, d);
  getMortuaryIotDevices = () => this.c.get('/mortuary/iot/devices');
  saveMortuaryIotDevice = (d: any) => this.c.post('/mortuary/iot/devices', d);
  saveMortuaryIotSensor = (d: any) => this.c.post('/mortuary/iot/sensors', d);
  getMortuaryIotAlerts = () => this.c.get('/mortuary/iot/alerts');
  getMortuaryIotReadings = (p?: any) => this.c.get('/mortuary/iot/readings', { params: p });
  ackMortuaryIotAlert = (alertId: string) => this.c.post(`/mortuary/iot/alerts/${alertId}/ack`);
  ingestMortuaryIot = (d: any) => this.c.post('/mortuary/iot/ingest', d);
  addMortuaryCalibration = (d: any) => this.c.post('/mortuary/iot/calibrations', d);
  getMortuaryIotIncidents = () => this.c.get('/mortuary/iot/incidents');
  resolveMortuaryIncident = (id: string, resolution: string) => this.c.post(`/mortuary/iot/incidents/${id}/resolve`, { resolution });
  getMortuaryMci = () => this.c.get('/mortuary/mci');
  createMortuaryMci = (d: any) => this.c.post('/mortuary/mci', d);
  bulkMortuaryMci = (mciId: string, items: any[]) => this.c.post(`/mortuary/mci/${mciId}/bodies`, { items });
  recordMortuaryScan = (d: any) => this.c.post('/mortuary/scan-events', d);
  getMortuaryChapels = () => this.c.get('/mortuary/chapel');
  getMortuaryChapelResources = () => this.c.get('/mortuary/chapel/resources');
  getMortuaryChapelBoard = (p?: any) => this.c.get('/mortuary/chapel/board', { params: p });
  getMortuaryChapelBooking = (id: string) => this.c.get(`/mortuary/chapel/bookings/${id}`);
  saveMortuaryChapel = (d: any) => this.c.post('/mortuary/chapel', d);
  bookMortuaryChapel = (d: any) => this.c.post('/mortuary/chapel/bookings', d);
  confirmMortuaryChapel = (id: string) => this.c.post(`/mortuary/chapel/bookings/${id}/confirm`);
  completeMortuaryChapel = (id: string) => this.c.post(`/mortuary/chapel/bookings/${id}/complete`);
  saveMortuaryRunSheet = (id: string, items: any[]) => this.c.post(`/mortuary/chapel/bookings/${id}/run-sheet`, { items });
  getMortuaryFuneralDirectors = () => this.c.get('/mortuary/funeral-directors');
  saveMortuaryFuneralDirector = (d: any) => this.c.post('/mortuary/funeral-directors', d);
  assignMortuaryFuneralDirector = (id: string, directorId: string) => this.c.post(`/mortuary/${id}/funeral-director`, { directorId });
  getMortuaryVehicles = () => this.c.get('/mortuary/transport/vehicles');
  getMortuaryDrivers = () => this.c.get('/mortuary/transport/drivers');
  getMortuaryTrips = () => this.c.get('/mortuary/transport/trips');
  saveMortuaryVehicle = (d: any) => this.c.post('/mortuary/transport/vehicles', d);
  saveMortuaryDriver = (d: any) => this.c.post('/mortuary/transport/drivers', d);
  scheduleMortuaryTrip = (d: any) => this.c.post('/mortuary/transport/trips', d);
  dispatchMortuaryTrip = (id: string) => this.c.post(`/mortuary/transport/trips/${id}/dispatch`);
  arriveMortuaryTrip = (id: string) => this.c.post(`/mortuary/transport/trips/${id}/arrive`);
  getMortuaryCemeteries = () => this.c.get('/mortuary/cemetery');
  getMortuaryPlots = (cemeteryId?: string) => this.c.get('/mortuary/cemetery/plots', { params: cemeteryId ? { cemeteryId } : {} });
  getMortuaryBurials = () => this.c.get('/mortuary/cemetery/burials');
  scheduleMortuaryBurial = (d: any) => this.c.post('/mortuary/cemetery/burials', d);
  completeMortuaryBurial = (id: string) => this.c.post(`/mortuary/cemetery/burials/${id}/complete`);
  getMortuaryCrematoria = () => this.c.get('/mortuary/crematorium');
  getMortuaryCrematoriumUnits = () => this.c.get('/mortuary/crematorium/units');
  getMortuaryCremations = () => this.c.get('/mortuary/crematorium/bookings');
  scheduleMortuaryCremation = (d: any) => this.c.post('/mortuary/crematorium/bookings', d);
  completeMortuaryCremation = (id: string) => this.c.post(`/mortuary/crematorium/bookings/${id}/complete`);
  handoverMortuaryUrn = (id: string, d: any) => this.c.post(`/mortuary/crematorium/urns/${id}/handover`, d);
  getMortuaryDispositions = () => this.c.get('/mortuary/final-disposition');
  recordMortuaryDisposition = (d: any) => this.c.post('/mortuary/final-disposition', d);
  inviteMortuaryPortal = (id: string, d: any) => this.c.post(`/mortuary/${id}/portal-invite`, d);
  getMortuaryPortal = (token: string) => this.c.get('/mortuary-portal', { params: { token } });
  getMortuaryCase360 = (id: string) => this.c.get(`/mortuary/${id}/case360`);
  getMortuaryTimeline = (id: string) => this.c.get(`/mortuary/${id}/timeline`);
  getMortuaryWorkflow = (id: string) => this.c.get(`/mortuary/${id}/workflow`);
  getMortuaryReleaseGate = (id: string) => this.c.get(`/mortuary/${id}/release-gate`);
  syncMortuaryLab = (id: string) => this.c.post(`/mortuary/${id}/lab-sync`);
  archiveMortuaryCase = (id: string, d?: any) => this.c.post(`/mortuary/${id}/archive`, d || {});
  searchMortuary = (p?: any) => this.c.get('/mortuary/search', { params: p });
  getMortuarySla = () => this.c.get('/mortuary/sla');
  getMortuaryExecutive = () => this.c.get('/mortuary/analytics/executive');
  getMortuaryLegalRules = () => this.c.get('/mortuary/legal-rules');
  transitionMortuaryBody = (id: string, d: any) => this.c.post(`/mortuary/${id}/transition`, d);
  addMortuaryPhoto = (id: string, d: any) => this.c.post(`/mortuary/${id}/photos`, d);
  syncMortuaryOffline = (events: any[]) => this.c.post('/mortuary/offline/sync', { events });
  cancelMortuaryChapel = (id: string) => this.c.patch(`/mortuary/chapel/bookings/${id}`, { status: 'CANCELLED' });
  saveMortuaryLegalRule = (d: any) => this.c.post('/mortuary/legal-rules', d);
  sweepMortuarySla = () => this.c.post('/mortuary/sla/sweep');

}

const api = new Api();
export default api;
